import React from "react";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { buildNextRecordNumber, mapCostCenterCodes } from "../lib/recordNumberUtils";
import { isAdminNotificationEnabled } from "../lib/adminNotificationPrefs";
import { buildProviderDirectory } from "../lib/peopleRoleAdapters";
import { choosePreferredOperationalRecord, isOperationalRecordDeleted } from "../lib/operationalRecordUtils";
import { fetchClimateAlertsBase, fetchClimateForecastBase, fetchFarmClimateSummary } from "../lib/climateApi";
import { getFarmClimateProfile, normalizeClimateStations } from "../lib/climateConfig";
import { buildOperationalClimatePanel, climateLevelMeta, resolveOperationalClimateContext } from "../lib/climateOperationalContext";
import { LIVE_ACTIVITY_EVENT_TYPES, LIVE_ACTIVITY_MODULES, notifyOperationalActivity } from "../lib/liveActivities";
import { stampRecordGeneration } from "../lib/farmGenerations";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";
import { DB } from "../lib/deviceStorage";
import { getLiveCollectionCache } from "../sync/cacheApi";

export function createServicesModule(deps){
  const {
    useState,
    useEffect,
    useSysLists,
    useSync,
    makeSeed,
    useFarmCollection,
    useGlobalCollection,
    useFarm,
    useToast,
    useReliableSyncOnSave,
    canCreateInModule,
    canEditInModule,
    canDeleteInModule,
    canViewCostCenter,
    canEditCostCenter,
    getAllowedCostCentersForModule,
    itemMatchesCostCenterAccess,
    SYSTEM_LISTS_DEFAULTS,
    enrichStockItemsWithQty,
    daysSince,
    dateColor,
    normalizeServiceType,
    getPriorityConfig,
    getPriorities,
    getEditableServiceStatuses,
    getAccounts,
    createClientId,
    today,
    fmtMoney,
    fmtDate,
    T,
    isCompactViewport,
    Btn,
    Field,
    Input,
    Select,
    Textarea,
    StatCard,
    Toast,
    Badge,
    ProvidersModule,
    ServiceDenyModal,
    ServicePhotoAddPhoneIcon,
    ServicePhotoStatusIcon,
    OCCURRENCE_CATEGORIES,
    SVC_STATUS_FLOW,
    SVC_TYPES,
    getMaterialUsageQty,
    getRestockableMaterials,
    hasCompletedMaterialRestock,
    hasPendingMaterialRestock,
    makeCreateMeta,
    makeUpdateMeta,
    compressImage,
  } = deps;

  const useSyncHook = typeof useSync === "function" ? useSync : function useNoSyncContext(){ return null; };

  return function ServicesModule({ session, isGestor, initialTab, mode="all", showProvidersTab=true, openRecordId, onClearOpenRecord, onBridgeBack }) {
  const SL       = useSysLists();
  const syncCtx  = useSyncHook();
  const costCenterCodeMap = React.useMemo(()=>mapCostCenterCodes(SL?.COST_CENTER_CODES || SYSTEM_LISTS_DEFAULTS.COST_CENTER_CODES || []), [SL]);
  const seed     = makeSeed();
  const [services,   setServices]   = useFarmCollection("services",    []);
  const [occurrences, setOccurrences] = useFarmCollection("occurrences",  []);
  const pendingOpenRecordIdRef = React.useRef(null);
  // v3.5.05: dedupe local do fallback oficial pós-ACK para Live de Services.
  const serviceLiveAckFallbackRef = React.useRef({});
  // v2.228.07: garante abertura única por deep link/Live e evita depender de callback externo instável
  const consumedOpenRecordIdRef = React.useRef(null);
  const onClearOpenRecordRef = React.useRef(onClearOpenRecord);
  const selectedRef = React.useRef(null);
  const servicesRef = React.useRef([]);
  const occurrencesRef = React.useRef([]);
  const [employees,  ]              = useFarmCollection("employees",    seed.employees);
  const [pastures,   ]              = useFarmCollection("pastures",     []);
  const [pois,       ]              = useFarmCollection("poi",          []);
  const [machinery,  ]              = useFarmCollection("machinery",    []);
  const [stockItems, ]              = useGlobalCollection("stock",      []);
  const { fid: currentFarmId, farm: currentFarm } = useFarm();
  const [stockMovs,  setStockMovs]  = useFarmCollection("stockMovements",[]);
  const [, setFinancialEntries] = useFarmCollection("financial", []);
  const [providers,  ]              = useGlobalCollection("providers",  []);
  const [people,     ]              = useGlobalCollection("people",     []);
  const [adminGlobalsConfigRows]    = useGlobalCollection("adminGlobalsConfig", []);
  const adminGlobalsConfig          = React.useMemo(()=> (adminGlobalsConfigRows||[])[0] || null, [adminGlobalsConfigRows]);
  const climateProfiles             = React.useMemo(()=> adminGlobalsConfig?.farmClimateProfiles || {}, [adminGlobalsConfig]);
  const farmClimateProfile          = React.useMemo(()=> getFarmClimateProfile(climateProfiles, currentFarmId), [climateProfiles, currentFarmId]);
  const climateStationCandidates    = React.useMemo(()=> normalizeClimateStations(farmClimateProfile?.stations, farmClimateProfile), [farmClimateProfile]);
  const providerDirectory           = React.useMemo(()=>buildProviderDirectory(people, providers), [people, providers]);
  const [, setNotifications]        = useFarmCollection("notifications", []);
  const [toast,      show]          = useToast();
  const syncOnSave                  = useReliableSyncOnSave();
  const isAdmin  = session?.role === "admin";
  const myName   = session?.name || "";
  const bridgeCostCenter = String(session?.sedeBridge?.enabled ? (session?.sedeBridge?.costCenter || "") : "").trim();
  const bridgeScoped = !!bridgeCostCenter;
  const matchesBridgeCostCenter = React.useCallback((item)=> !bridgeScoped || String(item?.costCenter || "").trim().toLowerCase()===bridgeCostCenter.toLowerCase(), [bridgeScoped, bridgeCostCenter]);
  const closedServiceStatuses = React.useMemo(()=> new Set(["Concluído","Cancelado","Negado","Concluída","Cancelada"]), []);
  const getServiceMaintenanceAnchor = React.useCallback((svc, fallbackTs=new Date().toISOString()) => (
    svc?.maintenanceStartedAt || svc?.startedAt || svc?.dateStart || svc?.date || svc?.createdAt || fallbackTs
  ), []);
  const normalizeServicePauseHistory = (svc) => Array.isArray(svc?.pauseHistory)
    ? svc.pauseHistory.filter(Boolean).map(entry=>({ ...entry }))
    : [];
  const buildServiceMaintenancePatch = React.useCallback((currentSvc, patch={}) => {
    const now = new Date().toISOString();
    const nextStatus = String(patch.status ?? currentSvc?.status ?? "").trim();
    const next = { ...patch };
    const pauseHistory = normalizeServicePauseHistory(currentSvc);
    const anchor = getServiceMaintenanceAnchor({ ...currentSvc, ...patch }, patch.startedAt || patch.completedAt || patch.dateEnd || now);
    if ((["Em Execução","Pausada","Concluído","Concluída"].includes(nextStatus) || !!patch.startedAt) && !currentSvc?.maintenanceStartedAt) {
      next.maintenanceStartedAt = anchor;
    }
    if (nextStatus === "Pausada") {
      const pauseStartedAt = patch.pausedAt || currentSvc?.pausedAt || now;
      const last = pauseHistory[pauseHistory.length - 1];
      if (!last || last.endedAt) {
        pauseHistory.push({
          startedAt: pauseStartedAt,
          reason: patch.pauseReason || currentSvc?.pauseReason || "",
          note: patch.pauseNote || currentSvc?.pauseNote || "",
          actor: myName,
        });
      } else {
        last.reason = patch.pauseReason || last.reason || currentSvc?.pauseReason || "";
        last.note = patch.pauseNote || last.note || currentSvc?.pauseNote || "";
        last.actor = last.actor || myName;
      }
      next.pauseHistory = pauseHistory;
    }
    if (nextStatus === "Em Execução") {
      const resumeAt = patch.resumedAt || now;
      const last = pauseHistory[pauseHistory.length - 1];
      if (last && !last.endedAt) last.endedAt = resumeAt;
      next.pauseHistory = pauseHistory;
      next.pausedAt = "";
    }
    if (closedServiceStatuses.has(nextStatus) || patch.completedAt || patch.dateEnd) {
      const endAt = patch.completedAt || patch.dateEnd || currentSvc?.completedAt || currentSvc?.dateEnd || now;
      const last = pauseHistory[pauseHistory.length - 1];
      if (last && !last.endedAt) last.endedAt = endAt;
      next.pauseHistory = pauseHistory;
      if (!next.maintenanceCompletedAt) next.maintenanceCompletedAt = endAt;
      if (!next.maintenanceStartedAt) next.maintenanceStartedAt = anchor;
    }
    return next;
  }, [closedServiceStatuses, getServiceMaintenanceAnchor, myName]);
  const permissionModuleId = mode==="external" ? "services_external" : "services";
  const canCreate = canCreateInModule(session,permissionModuleId) || canEditInModule(session,permissionModuleId);
  const canEditService = canEditInModule(session,permissionModuleId);
  const canDelete = canDeleteInModule(session,permissionModuleId);
  const allowedCostCenters = React.useMemo(()=> bridgeScoped ? [bridgeCostCenter] : getAllowedCostCentersForModule(session, permissionModuleId), [bridgeScoped, bridgeCostCenter, permissionModuleId, session]);
  const canApprove = canEditService || isGestor || isAdmin;
  const execTypeOptions = SL?.SERVICE_EXEC_TYPES || SYSTEM_LISTS_DEFAULTS.SERVICE_EXEC_TYPES || ["Interno (Colaborador)","Externo (Prestador)","Misto (Colaborador + Prestador)"];
  const pauseReasonOptions = SL?.TASK_PAUSE_REASONS || SYSTEM_LISTS_DEFAULTS.TASK_PAUSE_REASONS || [];
  const servicePhotoTitleOptions = React.useMemo(()=> SL?.SERVICE_PHOTO_TITLES || SYSTEM_LISTS_DEFAULTS.SERVICE_PHOTO_TITLES || [], [SL]);
  const enrichedStockItems = enrichStockItemsWithQty(stockItems, stockMovs);
  const normalizeParticipantName = React.useCallback((value) => String(typeof value === "string" ? value : (value?.name || value || "")).trim().toLowerCase(), []);
  const currentEmployee = React.useMemo(()=>{
    const sessionId = String(session?.id || "").trim();
    const sessionUserId = String(session?.userId || "").trim();
    const sessionUsername = String(session?.username || "").trim().toLowerCase();
    const sessionName = normalizeParticipantName(session?.name || myName || "");
    return (employees||[]).find(emp=>{
      const linked = String(emp?.linkedAccountId || emp?.accountId || "").trim();
      const userId = String(emp?.userId || "").trim();
      const username = String(emp?.username || "").trim().toLowerCase();
      const name = normalizeParticipantName(emp?.name || "");
      return (sessionId && linked===sessionId) || (sessionUserId && userId===sessionUserId) || (sessionUsername && username===sessionUsername) || (sessionName && name===sessionName);
    }) || null;
  }, [employees, session?.id, session?.userId, session?.username, session?.name, myName, normalizeParticipantName]);
  const currentIdentity = React.useMemo(()=>({
    accountId:String(session?.id||"").trim(),
    userId:String(session?.userId||"").trim(),
    employeeId:String(currentEmployee?.id||"").trim(),
    linkedAccountId:String(currentEmployee?.linkedAccountId || currentEmployee?.accountId || "").trim(),
    username:String(session?.username || currentEmployee?.username || "").trim().toLowerCase(),
    name:normalizeParticipantName(session?.name || currentEmployee?.name || myName || ""),
  }), [currentEmployee, session?.id, session?.userId, session?.username, session?.name, myName, normalizeParticipantName]);
  const participantMatchesMe = React.useCallback((value)=>{
    if(!value) return false;
    const obj = (value && typeof value === "object") ? value : { name: value };
    const ids = [obj?.id, obj?.employeeId, obj?.userId, obj?.linkedAccountId, obj?.accountId].map(v=>String(v||"").trim()).filter(Boolean);
    if(ids.some(v=> v===currentIdentity.employeeId || v===currentIdentity.accountId || v===currentIdentity.userId || v===currentIdentity.linkedAccountId)) return true;
    const username = String(obj?.username||"").trim().toLowerCase();
    if(username && username===currentIdentity.username) return true;
    const names = [obj?.name, obj?.displayName, obj].map(normalizeParticipantName).filter(Boolean);
    return names.includes(currentIdentity.name);
  }, [currentIdentity, normalizeParticipantName]);
  const isServiceParticipant = React.useCallback((svc) => {
    if(!svc) return false;
    const assignees = Array.isArray(svc?.assignees) ? svc.assignees : [];
    return assignees.some(participantMatchesMe)
      || participantMatchesMe({ id:svc?.contactPersonId, employeeId:svc?.contactPersonId, name:svc?.contactPerson, username:svc?.contactPersonUsername })
      || participantMatchesMe({ id:svc?.assignedToId, employeeId:svc?.assignedToId, name:svc?.assignedTo, username:svc?.assignedToUsername });
  }, [participantMatchesMe]);
  const canOperateService = React.useCallback((svc) => {
    if(!svc || isOperationalRecordDeleted(svc)) return false;
    if(isAdmin || isGestor) return true;
    return isServiceParticipant(svc);
  }, [isAdmin, isGestor, isServiceParticipant]);
  const canPerformServiceAction = React.useCallback((svc, patch={}) => {
    if(!svc || isOperationalRecordDeleted(svc)) return false;
    if(isAdmin || isGestor) return true;
    if(!isServiceParticipant(svc)) return false;
    const nextStatus = patch.status;
    if(!nextStatus) return true;
    return ["Agendado","Em Execução","Pausada","Concluído"].includes(nextStatus);
  }, [isAdmin, isGestor, isServiceParticipant]);
  const getServicePhotoEntries = (svc) => Array.isArray(svc?.servicePhotos) ? svc.servicePhotos : [];
  const getServicePhotoBatches = (svc) => {
    const entries = getServicePhotoEntries(svc);
    const groups = new Map();
    entries.forEach((ph, idx) => {
      const key = `${ph?.addedAt||"sem-data"}__${ph?.addedBy||"sem-autor"}`;
      if(!groups.has(key)) groups.set(key, { key, addedAt: ph?.addedAt||"", addedBy: ph?.addedBy||"", photos: [], firstIdx: idx });
      groups.get(key).photos.push({ ...ph, _idx: idx });
    });
    return Array.from(groups.values()).sort((a,b)=> String(b.addedAt||"").localeCompare(String(a.addedAt||"")) || a.firstIdx-b.firstIdx);
  };


  // ── Tab drives which "new" form type opens ──────────────────────
  const defaultTab = mode==="external" ? "external" : "internal";
  const [tab,        setTab]        = useState(initialTab || defaultTab);
  const [selected,   setSelected]   = useState(null);
  const [form,       setForm]       = useState(null);
  const [budgetForm, setBudgetForm] = useState(null);
  const [schedForm,  setSchedForm]  = useState(null);
  const [denyForm,   setDenyForm]   = useState(null);
  const [matForm,    setMatForm]    = useState(null); // {svcId, items:[{stockId,qty}]}
  const [serviceMaterialUsageDrafts, setServiceMaterialUsageDrafts] = useState({});
  const [pauseForm,  setPauseForm]  = useState(null);
  const [svcPhotoForm,setSvcPhotoForm]= useState(null);
  const [servicePhotoLightbox,setServicePhotoLightbox]= useState(null);
  const [svcRestockForm,setSvcRestockForm]= useState(null);
  const [nfForm,     setNfForm]     = useState(null); // {svcId, nfNum, nfPhoto, finalValue, dueDate, paymentMethod, justificationMissing}
  const [filterStatus,setFStatus]   = useState("Todos");
  const [filterType,setFType]       = useState("Todos");
  const [cardFilter,setCardFilter]  = useState("all");
  const [selectedClimatePanel, setSelectedClimatePanel] = useState(null);
  const [selectedClimateLoading, setSelectedClimateLoading] = useState(false);
  const [formClimatePanel, setFormClimatePanel] = useState(null);
  const [formClimateLoading, setFormClimateLoading] = useState(false);

  const renderClimateToneBadge = React.useCallback((tone, label, value)=>{
    const meta = climateLevelMeta(tone === "restrictive" ? "restrictive" : (tone === "favorable" ? "favorable" : "attention"));
    return <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:999,border:`1px solid ${meta.color}44`,background:`${meta.color}14`,color:meta.color,fontSize:11,fontWeight:700}}>{label}{value?`: ${value}`:""}</span>;
  }, []);

  const loadOperationalClimatePanel = React.useCallback(async (record, setter, loadingSetter) => {
    if (!currentFarmId || !record) { setter(null); return; }
    const context = resolveOperationalClimateContext(record, pastures, pois);
    loadingSetter(true);
    try {
      const requestBase = {
        farmId: currentFarmId,
        preferredSource: farmClimateProfile?.preferredSource || "wunderground",
        fallbackToInmet: farmClimateProfile?.fallbackToInmet !== false,
        latitude: context?.latitude,
        longitude: context?.longitude,
        stationCandidates: climateStationCandidates,
        contextType: context?.contextType || "farm",
        contextId: context?.contextId || "",
        contextName: context?.contextName || record?.location || record?.title || "",
        autoSync: true,
        userName: session?.name || "",
      };
      const [summary, forecast, alerts] = await Promise.all([
        fetchFarmClimateSummary({ ...requestBase, windowDays: farmClimateProfile?.analysisWindowDays || 30 }),
        fetchClimateForecastBase(requestBase),
        fetchClimateAlertsBase({ ...requestBase, alertRules: {
          heat_enabled: farmClimateProfile?.alertsHeatEnabled,
          heat_warning_temp_c: farmClimateProfile?.alertsHeatWarningTempC,
          heat_critical_temp_c: farmClimateProfile?.alertsHeatCriticalTempC,
          low_humidity_enabled: farmClimateProfile?.alertsLowHumidityEnabled,
          low_humidity_warning_pct: farmClimateProfile?.alertsLowHumidityWarningPct,
          low_humidity_critical_pct: farmClimateProfile?.alertsLowHumidityCriticalPct,
          heavy_rain_enabled: farmClimateProfile?.alertsHeavyRainEnabled,
          heavy_rain_warning_mm: farmClimateProfile?.alertsHeavyRainWarningMm,
          heavy_rain_critical_mm: farmClimateProfile?.alertsHeavyRainCriticalMm,
          dry_streak_enabled: farmClimateProfile?.alertsDryStreakEnabled,
          dry_streak_warning_days: farmClimateProfile?.alertsDryStreakWarningDays,
          dry_streak_critical_days: farmClimateProfile?.alertsDryStreakCriticalDays,
        } }),
      ]);
      setter(buildOperationalClimatePanel({
        summary, forecast, alerts, profile: farmClimateProfile,
        contextName: context?.contextName || record?.location || record?.title || "Fazenda",
      }));
    } catch (err) {
      setter({ error: err?.message || "Não foi possível consultar o clima operacional agora." });
    } finally {
      loadingSetter(false);
    }
  }, [currentFarmId, pastures, pois, farmClimateProfile, climateStationCandidates, session?.name]);

  React.useEffect(()=>{
    if(!selected) { setSelectedClimatePanel(null); setSelectedClimateLoading(false); return; }
    loadOperationalClimatePanel(selected, setSelectedClimatePanel, setSelectedClimateLoading);
  }, [selected, loadOperationalClimatePanel]);

  React.useEffect(()=>{
    if(!form) { setFormClimatePanel(null); setFormClimateLoading(false); return; }
    if(!(form.location || form.poiId)) { setFormClimatePanel(null); setFormClimateLoading(false); return; }
    loadOperationalClimatePanel(form, setFormClimatePanel, setFormClimateLoading);
  }, [form, loadOperationalClimatePanel]);

  const normalizeTimeline = React.useCallback((timeline)=> (Array.isArray(timeline)?timeline:[]).filter(Boolean), []);
  const dedupeCollection = React.useCallback((items, keyResolvers)=>{
    const list = Array.isArray(items) ? items.filter(Boolean) : [];
    const seen = new Map();
    list.forEach((item, index)=>{
      const keys = keyResolvers.map((fn)=>{
        try { return fn(item); } catch { return ""; }
      }).filter(Boolean);
      const key = keys[0] || `__idx_${index}`;
      const prev = seen.get(key);
      if(!prev) { seen.set(key, item); return; }
      seen.set(key, choosePreferredOperationalRecord(prev, item));
    });
    return Array.from(seen.values());
  }, []);
  const normalizeServiceRecord = React.useCallback((svc)=>{
    if(!svc) return null;
    const stableId = String(svc.id || createClientId(normalizeServiceType(svc)==="external" ? "extsvc" : "svc"));
    const farmId = svc.farmId || currentFarmId || "";
    return {
      ...svc,
      id: stableId,
      clientId: String(svc.clientId || stableId),
      farmId,
      timeline: normalizeTimeline(svc.timeline),
      materials: Array.isArray(svc.materials) ? svc.materials.filter(Boolean) : [],
      assignees: Array.isArray(svc.assignees) ? svc.assignees.filter(Boolean).map(a=> typeof a === "string" ? { name:a } : a) : [],
      photos: Array.isArray(svc.photos) ? svc.photos.filter(Boolean) : [],
      servicePhotos: Array.isArray(svc.servicePhotos) ? svc.servicePhotos.filter(Boolean) : [],
      nfPhotos: Array.isArray(svc.nfPhotos) ? svc.nfPhotos.filter(Boolean) : (svc.nfPhoto ? [svc.nfPhoto].filter(Boolean) : []),
      status: svc.status || (normalizeServiceType(svc)==="external" ? "Aguardando Aprovação" : "Agendado"),
      _deleted: !!(svc?._deleted || svc?.deletedAt || svc?.deleted_at),
      deletedAt: svc?.deletedAt || svc?.deleted_at || "",
      deleted_at: svc?.deletedAt || svc?.deleted_at || "",
      ...(!svc.createdAt ? makeCreateMeta(myName) : {}),
      ...(!svc.updatedAt ? { updatedAt: svc.createdAt || new Date().toISOString(), updatedBy: svc.updatedBy || myName } : {}),
    };
  }, [currentFarmId, myName, normalizeTimeline]);
  const normalizeOccurrenceRecord = React.useCallback((occ)=>{
    if(!occ) return null;
    const stableId = String(occ.id || createClientId("occ"));
    const farmId = occ.farmId || currentFarmId || "";
    return {
      ...occ,
      id: stableId,
      clientId: String(occ.clientId || stableId),
      farmId,
      timeline: normalizeTimeline(occ.timeline),
      materials: Array.isArray(occ.materials) ? occ.materials.filter(Boolean) : [],
      assignees: Array.isArray(occ.assignees) ? occ.assignees.filter(Boolean).map(a=> typeof a === "string" ? { name:a } : a) : [],
      photos: Array.isArray(occ.photos) ? occ.photos.filter(Boolean) : [],
      occurrencePhotos: Array.isArray(occ.occurrencePhotos) ? occ.occurrencePhotos.filter(Boolean) : [],
      status: occ.status || "Aberta",
      _deleted: !!(occ?._deleted || occ?.deletedAt || occ?.deleted_at),
      deletedAt: occ?.deletedAt || occ?.deleted_at || "",
      deleted_at: occ?.deletedAt || occ?.deleted_at || "",
      ...(!occ.createdAt ? makeCreateMeta(myName) : {}),
      ...(!occ.updatedAt ? { updatedAt: occ.createdAt || new Date().toISOString(), updatedBy: occ.updatedBy || myName } : {}),
    };
  }, [currentFarmId, myName, normalizeTimeline]);
  const normalizeServicesCollection = React.useCallback((items)=> dedupeCollection((Array.isArray(items)?items:[]).map(normalizeServiceRecord).filter(Boolean), [
    (svc)=>String(svc.clientId||""),
    (svc)=>String(svc.id||""),
    (svc)=>`${String(svc.type||"").toLowerCase()}__${String(svc.title||"").trim().toLowerCase()}__${String(svc.createdAt||"")}`,
  ]), [dedupeCollection, normalizeServiceRecord]);
  const ensureServiceRecordNumbers = React.useCallback((records)=>{
    const baseRecords = Array.isArray(records) ? records.slice() : [];
    const sorted = baseRecords.map((record, index)=>({ record, index, stamp: String(record?.createdAt || record?.updatedAt || index) }))
      .sort((a,b)=> String(a.stamp).localeCompare(String(b.stamp)));
    let changed = false;
    const working = baseRecords.slice();
    sorted.forEach(({ record, index })=>{
      if (!record || record.recordNumber) return;
      const moduleKey = normalizeServiceType(record)==="external" ? "externalServices" : "services";
      const next = buildNextRecordNumber(working.filter(Boolean), { moduleKey, costCenter:record?.costCenter, costCenterCodeMap });
      working[index] = { ...record, ...next };
      changed = true;
    });
    return changed ? working : baseRecords;
  }, [costCenterCodeMap]);
  const normalizeOccurrencesCollection = React.useCallback((items)=> dedupeCollection((Array.isArray(items)?items:[]).map(normalizeOccurrenceRecord).filter(Boolean), [
    (occ)=>String(occ.clientId||""),
    (occ)=>String(occ.id||""),
    (occ)=>`${String(occ.title||occ.description||"").trim().toLowerCase()}__${String(occ.location||"").trim().toLowerCase()}__${String(occ.reportedAt||occ.createdAt||"")}`,
  ]), [dedupeCollection, normalizeOccurrenceRecord]);
  const normalizeStockMovementRecord = React.useCallback((mov)=>{
    if(!mov) return null;
    const stableId = String(mov.clientId || mov.id || createClientId("smv"));
    return {
      ...mov,
      id: String(mov.id || stableId),
      clientId: stableId,
      farmId: mov.farmId || currentFarmId || "",
      qty: Number(mov.qty || 0),
      updatedAt: mov.updatedAt || mov.ts || mov.createdAt || new Date().toISOString(),
      by: mov.by || myName,
    };
  }, [currentFarmId, myName]);
  const normalizeStockMovementsCollection = React.useCallback((items)=> dedupeCollection((Array.isArray(items)?items:[]).map(normalizeStockMovementRecord).filter(Boolean), [
    (mov)=>String(mov.clientId||""),
    (mov)=>String(mov.id||""),
    (mov)=>`${String(mov.itemId||"")}__${String(mov.origin||"")}__${String(mov.originId||"")}__${String(mov.type||"")}__${String(mov.ts||mov.updatedAt||"")}`,
  ]), [dedupeCollection, normalizeStockMovementRecord]);
  const applyServices = React.useCallback((updater)=>{
    setServices(prev=>ensureServiceRecordNumbers(normalizeServicesCollection(typeof updater === "function" ? updater(Array.isArray(prev)?prev:[]) : updater)));
  }, [normalizeServicesCollection, ensureServiceRecordNumbers, setServices]);
  const applyOccurrences = React.useCallback((updater)=>{
    setOccurrences(prev=>normalizeOccurrencesCollection(typeof updater === "function" ? updater(Array.isArray(prev)?prev:[]) : updater));
  }, [normalizeOccurrencesCollection, setOccurrences]);
  const applyStockMovements = React.useCallback((updater)=>{
    setStockMovs(prev=>normalizeStockMovementsCollection(typeof updater === "function" ? updater(Array.isArray(prev)?prev:[]) : updater));
  }, [normalizeStockMovementsCollection, setStockMovs]);

  const makeProvisionalRecordNumber = React.useCallback((records, options)=>{
    const next = buildNextRecordNumber(Array.isArray(records) ? records.filter(Boolean) : [], options || {});
    return {
      ...next,
      recordNumber: appendProvisionalSuffix(next.recordNumber),
      recordNumberStatus: "provisional",
    };
  }, []);

  const makeTimelineEntry = React.useCallback((entry = {}, at = null) => {
    const stableId = String(entry.clientId || entry.id || createClientId("tl"));
    return {
      ...entry,
      id: String(entry.id || stableId),
      clientId: stableId,
      ts: String(entry.ts || at || new Date().toISOString()),
      actor: String(entry.actor || myName || "Sistema"),
    };
  }, [myName]);

  const makeServiceMaterialEntry = React.useCallback((entry = {}, at = null) => {
    const stableId = String(entry.clientId || entry.id || createClientId("mat"));
    return {
      ...entry,
      id: String(entry.id || stableId),
      clientId: stableId,
      ts: String(entry.ts || at || new Date().toISOString()),
      by: String(entry.by || myName || ""),
    };
  }, [myName]);

  const makeStockMovementEntry = React.useCallback((entry = {}, at = null) => {
    const stableId = String(entry.clientId || entry.id || createClientId("smv"));
    const ts = String(entry.ts || at || new Date().toISOString());
    return {
      ...entry,
      id: String(entry.id || stableId),
      clientId: stableId,
      ts,
      updatedAt: String(entry.updatedAt || ts),
      by: String(entry.by || myName || ""),
      farmId: String(entry.farmId || currentFarmId || ""),
    };
  }, [currentFarmId, myName]);

  const markServiceUpdatePending = React.useCallback((record, at, operationType = "update")=> markOperationalUpdatePending(record, { userName: myName, at, operationType }), [myName]);
  const markServiceCreatePending = React.useCallback((record, at)=> markOperationalCreatePending(record, { userName: myName, at }), [myName]);

  const appendFinancialEntry = React.useCallback((entry)=>{
    const payload = {
      id:createClientId('fin'),
      farmId: currentFarmId || '',
      automatic:true,
      originModule:'services_external',
      originId: entry.originId || '',
      type:'Despesa',
      category:'Serviços Terceirizados',
      accountCode: entry.accountCode || '4.1.03',
      accountName: entry.accountName || 'Serviços Terceirizados',
      description: entry.description || '',
      amount:Number(entry.amount || 0),
      date: entry.date || today(),
      dueDate: entry.dueDate || entry.date || today(),
      paymentMethod: entry.paymentMethod || '',
      documentNumber: entry.documentNumber || '',
      costCenter: entry.costCenter || 'Sede',
      counterCostCenter: entry.counterCostCenter || '',
      status: entry.status || 'Lançado',
      notes: entry.notes || '',
      justificationMissing: entry.justificationMissing || '',
      taxModel: entry.taxModel || 'Atual',
      metadata: entry.metadata || {},
      ...makeCreateMeta(myName),
    };
    setFinancialEntries(prev=>[...(Array.isArray(prev)?prev:[]), payload]);
    syncOnSave('financial');
  }, [currentFarmId, myName, setFinancialEntries, syncOnSave]);

  const buildServiceNotificationRecipients = React.useCallback(async (serviceRecord)=>{
    const loadedAccounts = await getAccounts?.();
    const accounts = Array.isArray(loadedAccounts) ? loadedAccounts : [];
    const farmId = String(serviceRecord?.farmId || currentFarmId || "").trim();
    const creatorUsername = String(session?.username || "").trim().toLowerCase();
    const creatorAccountId = String(session?.id || "").trim();
    const canAccessFarm = (acc)=>{
      const farmIds = Array.isArray(acc?.farmIds) ? acc.farmIds.filter(Boolean) : [];
      return !farmId || !farmIds.length || farmIds.includes(farmId);
    };
    const recipientsMap = new Map();
    const pushRecipient = (acc, roleOverride="")=>{
      if(!acc) return;
      const accountId = String(acc?.id || "").trim();
      const username = String(acc?.username || "").trim().toLowerCase();
      if(!accountId && !username) return;
      const key = accountId || username;
      if(recipientsMap.has(key)) return;
      recipientsMap.set(key, {
        accountId,
        userId: String(acc?.userId || "").trim(),
        username,
        role: String(roleOverride || acc?.role || "").trim().toLowerCase(),
        name: String(acc?.name || "").trim(),
      });
    };
    pushRecipient({ id: creatorAccountId, userId: session?.userId || "", username: creatorUsername, role: session?.role || "solicitante", name: session?.name || serviceRecord?.requester || "" }, session?.role || "solicitante");
    accounts.filter(acc=>String(acc?.role || "").trim().toLowerCase()==="admin" && acc?.active!==false).forEach(acc=>pushRecipient(acc, "admin"));
    accounts.filter(acc=>String(acc?.role || "").trim().toLowerCase()==="gestor" && acc?.active!==false && canAccessFarm(acc)).forEach(acc=>pushRecipient(acc, "gestor"));
    return Array.from(recipientsMap.values());
  }, [currentFarmId, session?.id, session?.name, session?.role, session?.userId, session?.username]);


  const buildServiceLiveActivityDisplay = React.useCallback((serviceRecord={}, meta={})=>{
    const serviceKind = normalizeServiceType(serviceRecord);
    const isExternal = serviceKind === "external";
    const priorityMeta = getPriorityConfig(serviceKind==="occurrence" ? "occurrence" : (isExternal ? "external" : "internal"), serviceRecord?.priority || meta?.priority || "normal");
    const stepperLabels = isExternal
      ? ["Aberto", "Orçamento", "Aprovação", "Execução", "Concluído"]
      : ["Aberto", "Agendado", "Execução", "Pausa", "Concluído"];
    const statusText = String(serviceRecord?.status || meta?.status || "Aberto").trim();
    const stepperCurrent = isExternal
      ? (statusText.includes("Orçamento") ? "Orçamento"
        : statusText.includes("Aprovação") || statusText === "Aprovado" || statusText === "Negado" ? "Aprovação"
        : statusText.includes("Execução") ? "Execução"
        : statusText.includes("Paus") ? "Execução"
        : statusText.includes("Concl") ? "Concluído"
        : "Aberto")
      : (statusText.includes("Concl") ? "Concluído"
        : statusText.includes("Paus") ? "Pausa"
        : statusText.includes("Execução") || statusText.includes("Aprov") || statusText.includes("Orçamento") ? "Execução"
        : statusText.includes("Agend") ? "Agendado"
        : "Aberto");
    return {
      displayTitle: "Trembo - Serviço",
      displayBadge: priorityMeta?.label || "Normal",
      recordCode: serviceRecord?.recordNumber || serviceRecord?.id || "",
      displayDate: fmtDate(serviceRecord?.date || serviceRecord?.dateStart || serviceRecord?.createdAt || serviceRecord?.updatedAt || ""),
      costCenterName: serviceRecord?.costCenter || "",
      stepperLabels,
      stepperCurrent,
    };
  }, []);

  // v2.228.05: exibe recordNumber com sufixo -UDT enquanto há atualização pendente.
  // -PRV já fica no recordNumber durante criação pendente.
  function displayServiceRecordNumber(record) {
    if (!record) return "—";
    const rn = String(record.recordNumber || record.id || "");
    if (!rn) return "—";
    if (record.pendingLocalUpdate && !record.pendingLocalCreate) {
      return appendUpdatePendingDisplaySuffix(rn);
    }
    return rn;
  }

  const appendServiceNotification = React.useCallback(async (serviceRecord, meta={}) => {
    try{
      serviceRecord = stampRecordGeneration(serviceRecord, currentFarm);
      const actor = session?.name || serviceRecord?.requester || "Sistema";
      const eventAt = String(meta?.eventAt || serviceRecord?.updatedAt || serviceRecord?.createdAt || new Date().toISOString()).trim() || new Date().toISOString();
      const liveDisplay = buildServiceLiveActivityDisplay(serviceRecord, meta);
      const serviceKindForLive = normalizeServiceType(serviceRecord);
      const liveModuleKey = serviceKindForLive === "occurrence" ? LIVE_ACTIVITY_MODULES.OCCURRENCES : LIVE_ACTIVITY_MODULES.SERVICES;
      const liveSettingsFromAdmin = adminGlobalsConfig?.liveActivitySettings;
      const liveGloballyDisabled = liveSettingsFromAdmin?.enabled === false;
      const liveModuleDisabled = liveSettingsFromAdmin?.modules?.[liveModuleKey] === false;
      // v3.5.04: Live de services fica server-driven, igual Compras.
      // Enquanto o registro ainda tem PRV/UDT/DEL local, o app não deve iniciar/atualizar
      // Live Activity com número provisório. O servidor dispara a Live após push_ops + ACK,
      // já com recordNumber oficial e estado confirmado.
      const hasPendingOperationalState = !!(
        serviceRecord?.pendingLocalCreate ||
        serviceRecord?.pendingLocalUpdate ||
        serviceRecord?.pendingLocalDelete ||
        serviceRecord?.pendingLocalChange ||
        serviceRecord?.updatePending ||
        String(serviceRecord?.recordNumber || "").toUpperCase().endsWith("-PRV")
      );
      const blockLocalLiveUntilServerAck = liveModuleKey === LIVE_ACTIVITY_MODULES.SERVICES && hasPendingOperationalState;
      if (!liveGloballyDisabled && !liveModuleDisabled && !blockLocalLiveUntilServerAck) notifyOperationalActivity({
        module: liveModuleKey,
        eventType: String(meta?.event || "").includes("created") ? LIVE_ACTIVITY_EVENT_TYPES.CREATED : LIVE_ACTIVITY_EVENT_TYPES.STATUS_CHANGED,
        record: serviceRecord,
        title: liveDisplay.displayTitle || String(meta?.title || "Atualização de serviço").trim() || "Atualização de serviço",
        status: meta?.status || serviceRecord?.status || "Aberto",
        previousStatus: meta?.previousStatus || "",
        farmId: String(serviceRecord?.farmId || currentFarmId || "").trim(),
        farmName: serviceRecord?.farmName || currentFarm?.name || "",
        extraFarmShortCode: currentFarm?.shortCode || currentFarm?.sigla || serviceRecord?.farmShortCode || serviceRecord?.farmSigla || "",
        updatedAt: eventAt,
        actor,
        priority: serviceRecord?.priority || meta?.priority || "normal",
        ...liveDisplay,
        extra: { event: String(meta?.event || "service_updated").trim() || "service_updated", eventLabel: String(meta?.eventLabel || "Atualização do serviço").trim() || "Atualização do serviço", recordNumber: serviceRecord?.recordNumber || "", serviceType: normalizeServiceType(serviceRecord), ...liveDisplay },
      })?.catch?.(() => {});
      if(!isAdminNotificationEnabled(adminGlobalsConfig, normalizeServiceType(serviceRecord)==="external" ? "services_external" : "services")) return;
      const recipients = await buildServiceNotificationRecipients(serviceRecord);
      if(!recipients.length) return;
      const summary = String(meta?.summary || meta?.body || "").trim() || `${actor} atualizou ${meta?.entityLabel || "o registro"}${serviceRecord?.costCenter ? ` · ${serviceRecord.costCenter}` : ""}.`;
      const notifId = createClientId("ntf");
      const notification = {
        id:notifId,
        clientId:notifId,
        farmId:String(serviceRecord?.farmId || currentFarmId || "").trim(),
        type:String(meta?.type || "service_event").trim() || "service_event",
        event:String(meta?.event || "service_updated").trim() || "service_updated",
        eventLabel:String(meta?.eventLabel || "Atualização do serviço").trim() || "Atualização do serviço",
        title:String(meta?.title || "Atualização de serviço").trim() || "Atualização de serviço",
        summary,
        body:String(meta?.body || summary).trim() || summary,
        orderId: serviceRecord?.id || "",
        orderRecordNumber: serviceRecord?.recordNumber || "",
        entityType: String(meta?.entityType || (normalizeServiceType(serviceRecord)==="external" ? "service_external" : "service_internal")).trim(),
        entityId: serviceRecord?.id || "",
        entityRecordNumber: serviceRecord?.recordNumber || "",
        notificationModuleId: String(meta?.notificationModuleId || (normalizeServiceType(serviceRecord)==="external" ? "services_external" : "services")).trim(),
        notificationSectionKey: String(meta?.notificationSectionKey || (normalizeServiceType(serviceRecord)==="external" ? "outsourced" : "services")).trim(),
        status: meta?.status || serviceRecord?.status || "Aberto",
        orderStatus: meta?.status || serviceRecord?.status || "Aberto",
        previousOrderStatus: meta?.previousStatus || "",
        costCenter: serviceRecord?.costCenter || "",
        requester: serviceRecord?.requester || actor,
        recipients,
        openedInApp:false,
        readBy:[],
        eventAt,
        ...makeCreateMeta(session?.name || "", { updatedAt:eventAt }),
      };
      setNotifications(prev=>[...(Array.isArray(prev)?prev:[]), notification]);
      syncOnSave("notifications");
    }catch(e){}
  }, [adminGlobalsConfig, buildServiceLiveActivityDisplay, buildServiceNotificationRecipients, currentFarm, currentFarmId, session?.name, setNotifications, syncOnSave]);


  // v3.5.05: quando o push_ops recebe ACK e oficializa o número no app nativo,
  // disparar uma Live local apenas como fallback pós-ACK, já com recordNumber oficial.
  // Isso corrige o caso em que bloquear a Live PRV impedia a Live de nascer quando
  // o serviço era iniciado no próprio app nativo, sem voltar a exibir número provisório.
  React.useEffect(() => {
    const onServicesHydratedAfterAck = (event) => {
      const detail = event?.detail || {};
      if (detail?.collection && detail.collection !== "services") return;
      if (detail?.source !== "operational-ack" && detail?.source !== "live-service-hydrate") return;
      const fid = String(detail?.farmId || currentFarmId || "").trim();
      if (!fid) return;
      setTimeout(() => {
        (async () => {
          try {
            const localKey = `fz:${fid}:services`;
            const liveList = getLiveCollectionCache(localKey);
            const dbList = await DB.get(localKey).catch(() => []);
            const stateList = Array.isArray(servicesRef.current) ? servicesRef.current : [];
            const mergedById = new Map();
            const addList = (arr) => {
              if (!Array.isArray(arr)) return;
              arr.forEach((svc) => {
                if (!svc || typeof svc !== "object") return;
                const id = String(svc?.id || svc?.clientId || svc?.recordId || svc?.recordNumber || Math.random()).trim();
                mergedById.set(id, { ...(mergedById.get(id) || {}), ...svc });
              });
            };
            // v3.5.07: no app nativo, o ACK pode atualizar IDB/live cache antes do
            // React state da lista. Usar as três fontes evita perder a janela pós-ACK
            // e garante Live local com número oficial mesmo sem sair/entrar no módulo.
            addList(stateList);
            addList(dbList);
            addList(liveList);
            const list = Array.from(mergedById.values());
            const ackIds = new Set((Array.isArray(detail?.acknowledgedOperations) ? detail.acknowledgedOperations : [])
              .flatMap((ack) => [ack?.id, ack?.clientId, ack?.recordId, ack?.operationId, ack?.pendingOperationId, ack?.serverAckOperationId, ack?.ackedOperationId])
              .map((v) => String(v || "").trim())
              .filter(Boolean));
            const candidates = list.filter((svc) => {
              if (!svc) return false;
              if (String(svc?.farmId || fid || "").trim() !== fid) return false;
              if (ackIds.size === 0) return true;
              const ids = [svc?.id, svc?.clientId, svc?.recordId, svc?.pendingOperationId, svc?.localOperationId, svc?.serverAckOperationId, svc?.ackedOperationId]
                .map((v) => String(v || "").trim())
                .filter(Boolean);
              return ids.some((id) => ackIds.has(id));
            });
            const official = candidates.filter((svc) => {
              const rn = String(svc?.recordNumber || "").trim();
              if (!rn || rn.toUpperCase().endsWith("-PRV")) return false;
              if (svc?.pendingLocalCreate || svc?.pendingLocalUpdate || svc?.pendingLocalDelete || svc?.pendingLocalChange || svc?.updatePending) return false;
              if (svc?._deleted || svc?.deletedAt || svc?.deleted_at) return false;
              return !!svc?.serverConfirmed || !!svc?.serverAckAt || String(svc?.syncStatus || "").toLowerCase() === "synced";
            });
            const recent = official.sort((a, b) => String(b?.serverAckAt || b?.updatedAt || b?.createdAt || "").localeCompare(String(a?.serverAckAt || a?.updatedAt || a?.createdAt || "")))[0];
            if (!recent) return;
            const recId = String(recent?.id || recent?.clientId || recent?.recordId || recent?.recordNumber || "").trim();
            const rn = String(recent?.recordNumber || "").trim();
            const key = `${fid}:${recId}:${rn}`;
            const last = Number(serviceLiveAckFallbackRef.current[key] || 0);
            if (last && Date.now() - last < 45000) return;
            serviceLiveAckFallbackRef.current = { ...serviceLiveAckFallbackRef.current, [key]: Date.now() };
            appendServiceNotification(recent, {
              event: "service_created_ack_official",
              eventLabel: "Serviço confirmado",
              title: `${normalizeServiceType(recent)==="external" ? "Serviço terceirizado" : "Serviço"} confirmado`,
              summary: `Serviço ${rn || recId} confirmado pelo servidor.`,
              status: recent?.status || "Aberto",
              entityLabel: normalizeServiceType(recent)==="external" ? "o serviço terceirizado" : "o serviço",
              eventAt: recent?.serverAckAt || recent?.updatedAt || new Date().toISOString(),
            });
          } catch (_err) {}
        })();
      }, 900);
    };
    window.addEventListener("fz:services:hydrated", onServicesHydratedAfterAck);
    return () => window.removeEventListener("fz:services:hydrated", onServicesHydratedAfterAck);
  }, [appendServiceNotification, currentFarmId]);

  const createServiceNotifications = React.useCallback(async (serviceRecord) => {
    const actor = session?.name || serviceRecord?.requester || "Sistema";
    const recordLabel = serviceRecord?.recordNumber || serviceRecord?.id || "registro";
    const entityLabel = normalizeServiceType(serviceRecord)==="external" ? "o serviço terceirizado" : "o serviço";
    const summary = `${actor} criou ${entityLabel} ${recordLabel}${serviceRecord?.costCenter ? ` · ${serviceRecord.costCenter}` : ""}.`;
    await appendServiceNotification(serviceRecord, {
      event:"service_created",
      eventLabel:"Registro criado",
      title:`${normalizeServiceType(serviceRecord)==="external" ? "Serviço terceirizado" : "Serviço"} criado`,
      summary,
      body: summary,
      status: serviceRecord?.status || "Aberto",
      entityLabel,
      eventAt: serviceRecord?.updatedAt || serviceRecord?.createdAt || new Date().toISOString(),
    });
  }, [appendServiceNotification, session?.name]);

  const notifyServiceStatusChange = React.useCallback(async (previousService, nextService, eventMeta={}) => {
    const previousStatus = String(previousService?.status || "").trim();
    const nextStatus = String(nextService?.status || "").trim();
    if(!nextStatus || previousStatus === nextStatus) return;
    const actor = session?.name || nextService?.requester || "Sistema";
    const recordLabel = nextService?.recordNumber || nextService?.id || "";
    const entityLabel = normalizeServiceType(nextService)==="external" ? "serviço terceirizado" : "serviço";
    const defaultSummary = `${actor} atualizou o status do ${entityLabel} ${recordLabel} de ${previousStatus || "sem status"} para ${nextStatus}.`;
    await appendServiceNotification(nextService, {
      event:eventMeta?.event || "service_status_changed",
      eventLabel:eventMeta?.eventLabel || "Status atualizado",
      title:eventMeta?.title || `${normalizeServiceType(nextService)==="external" ? "Serviço terceirizado" : "Serviço"} ${nextStatus}`,
      summary:eventMeta?.summary || defaultSummary,
      body:eventMeta?.body || eventMeta?.summary || defaultSummary,
      status:nextStatus,
      previousStatus,
      entityLabel,
      eventAt:eventMeta?.eventAt || nextService?.updatedAt || new Date().toISOString(),
    });
  }, [appendServiceNotification, session?.name]);

  const updateSvc = React.useCallback((id, patch, entry) => {
    const currentSvc = services.find(s=>s.id===id);
    if(!currentSvc){ show("Serviço não encontrado.","error"); return; }
    const isOperationalAction = Object.prototype.hasOwnProperty.call(patch||{},"status") || Object.prototype.hasOwnProperty.call(patch||{},"scheduledDate");
    if(isOperationalAction){
      if(!canPerformServiceAction(currentSvc, patch||{})){
        show("Somente colaboradores designados podem executar esta ação neste serviço.","error");
        return;
      }
    } else if(!canEditService){
      show("Sem permissão para editar serviços.","error");
      return;
    }
    const _ts=new Date().toISOString();
    let nextSvc = null;
    applyServices(prev=>prev.map(s=>{
      if(s.id!==id) return s;
      const tl=[...(s.timeline||[]),...(entry?[makeTimelineEntry(entry, _ts)]:[])];
      const maintenancePatch = buildServiceMaintenancePatch(s, patch||{});
      nextSvc = markServiceUpdatePending({...s,...patch,...maintenancePatch,timeline:tl,...makeUpdateMeta(myName)}, _ts, "service_action");
      return nextSvc;
    }));
    if(nextSvc){ notifyServiceStatusChange(currentSvc, nextSvc, { eventAt:_ts }); }
    syncOnSave("services");
  }, [applyServices, buildServiceMaintenancePatch, canEditService, canPerformServiceAction, makeTimelineEntry, markServiceUpdatePending, myName, notifyServiceStatusChange, services, show, syncOnSave]);

  const canAddPhotosToService = React.useCallback((svc) => {
    if(!svc) return false;
    if(!(canOperateService(svc) || canEditService || isAdmin || isGestor)) return false;
    if(["Cancelado","Negado"].includes(svc.status)) return false;
    if(svc.status !== "Concluído") return true;
    const baseDate = svc.completedAt || svc.updatedAt || svc.createdAt;
    if(!baseDate) return false;
    return daysSince(baseDate) <= 7;
  }, [canEditService, canOperateService, isAdmin, isGestor]);

  const openInlineNfForm = React.useCallback((svcId)=>{
    const svc = services.find(s=>String(s.id)===String(svcId));
    setNfForm({
      svcId,
      nfNum: svc?.nfNum || "",
      nfPhoto: svc?.nfPhoto || "",
      finalValue: String(svc?.finalValue || svc?.budget?.value || ""),
      dueDate: svc?.finalDueDate || today(),
      paymentMethod: svc?.finalPaymentMethod || svc?.budget?.payMethod || "",
      justificationMissing: svc?.accountingJustification || "",
    });
  }, [services]);

  const pickInlineNfPhoto = React.useCallback(()=>{
    const inp=document.createElement("input"); inp.type="file"; inp.accept="image/*";
    inp.onchange=e=>{
      const f=e.target.files && e.target.files[0];
      if(!f) return;
      const r=new FileReader();
      r.onload=ev=>setNfForm(prev=> prev ? ({...prev, nfPhoto: ev.target.result}) : prev);
      r.readAsDataURL(f);
    };
    inp.click();
  }, []);

  const saveInlineNf = React.useCallback(()=>{
    if(!nfForm?.svcId) return;
    const svc = services.find(s=>s.id===nfForm.svcId);
    const resolvedAmount = Number(nfForm.finalValue || svc?.budget?.value || 0);
    updateSvc(nfForm.svcId,{nfNum:nfForm.nfNum||"",nfPhoto:nfForm.nfPhoto||"",status:"Concluído",completedAt:new Date().toISOString(),nfPending:false,finalValue:resolvedAmount || 0,finalDueDate:nfForm.dueDate||"",finalPaymentMethod:nfForm.paymentMethod||svc?.budget?.payMethod||"",accountingJustification:nfForm.justificationMissing||""},
      {action:"Nota fiscal registrada",note:`NF: ${nfForm.nfNum||"s/n"}${resolvedAmount?` · R$ ${fmtMoney(resolvedAmount)}`:""}`});
    appendFinancialEntry({ originId:`${nfForm.svcId}:final:${Date.now()}`, description:`Serviço terceirizado concluído · ${svc?.title||nfForm.svcId}`, amount:resolvedAmount, documentNumber:nfForm.nfNum||'', costCenter:svc?.costCenter||svc?.category||'Sede', paymentMethod:nfForm.paymentMethod||svc?.budget?.payMethod||'', dueDate:nfForm.dueDate||today(), status:nfForm.paymentMethod ? 'Agendado' : 'Lançado', notes:`Prestador: ${svc?.budget?.provider||svc?.providerName||''}`, justificationMissing: nfForm.justificationMissing || (resolvedAmount ? '' : 'Serviço concluído sem valor final informado; revisar no financeiro.'), metadata:{ stage:'service_completed', serviceId:nfForm.svcId, provider:svc?.budget?.provider||svc?.providerName||'', nfPhoto:!!nfForm.nfPhoto } });
    show("✅ Nota fiscal registrada!");
    setNfForm(null);
  }, [appendFinancialEntry, nfForm, services, show, updateSvc]);

  const openInlineServicePhotos = React.useCallback((svcId)=>{
    const initialTitle = servicePhotoTitleOptions[0] || "";
    setSvcPhotoForm({
      svcId,
      rows:[{ id: Date.now(), title: initialTitle, photo: "", thumb: "", originalName: "" }],
    });
  }, [servicePhotoTitleOptions]);

  const pickInlineServicePhoto = React.useCallback(async (rowId)=>{
    const inp=document.createElement("input"); inp.type="file"; inp.accept="image/*";
    inp.onchange=async e=>{
      const f=e.target.files && e.target.files[0];
      if(!f) return;
      const photo = await compressImage(f, 1400, 0.78);
      const thumb = await compressImage(f, 320, 0.64);
      setSvcPhotoForm(prev=> prev ? ({
        ...prev,
        rows:(prev.rows||[]).map(row=>row.id===rowId?{...row,photo,thumb,originalName:f.name||""}:row)
      }) : prev);
    };
    inp.click();
  }, []);

  const saveInlineServicePhotos = React.useCallback(()=>{
    if(!svcPhotoForm?.svcId) return;
    const currentSvc = services.find(s=>s.id===svcPhotoForm.svcId);
    if(!currentSvc){ show("Serviço não encontrado.","error"); return; }
    if(!canAddPhotosToService(currentSvc)){ show("As fotos só podem ser adicionadas até 7 dias após a conclusão do serviço.","error"); return; }
    const existingEntries = getServicePhotoEntries(currentSvc);
    const valid = (svcPhotoForm.rows||[]).map(r=>({
      id: `svcphoto_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      title: (r.title||"").trim(),
      photo: r.photo||"",
      thumb: r.thumb || r.photo || "",
      addedAt: new Date().toISOString(),
      addedBy: myName,
      source: "database",
      originalName: r.originalName || "",
    })).filter(r=>r.title && r.photo);
    if(!valid.length){ show("Adicione ao menos uma foto com título.","error"); return; }
    if(existingEntries.length + valid.length > 10){ show("Cada serviço pode ter no máximo 10 fotos.","error"); return; }
    updateSvc(svcPhotoForm.svcId,{ servicePhotos:[...existingEntries, ...valid] },{action:"Fotos adicionadas ao serviço",note:valid.map(v=>v.title).join(", ")});
    show("✅ Fotos adicionadas e salvas no banco do serviço!");
    setSvcPhotoForm(null);
  }, [canAddPhotosToService, myName, services, show, svcPhotoForm, updateSvc]);




  useEffect(()=>{
    const allowed = mode==="external" ? ["external","providers"] : ["internal"];
    if(initialTab && allowed.includes(initialTab)) setTab(initialTab);
    else if(!allowed.includes(tab)) setTab(defaultTab);
  },[initialTab, mode, defaultTab, tab]);

  useEffect(()=>{
    const normalized = normalizeServicesCollection(services);
    if(JSON.stringify(normalized)!==JSON.stringify(services)){
      setServices(normalized);
    }
  }, [normalizeServicesCollection, services, setServices]);

  useEffect(()=>{
    const normalized = normalizeOccurrencesCollection(occurrences);
    if(JSON.stringify(normalized)!==JSON.stringify(occurrences)){
      setOccurrences(normalized);
    }
  }, [normalizeOccurrencesCollection, occurrences, setOccurrences]);

  useEffect(()=>{
    const normalized = normalizeStockMovementsCollection(stockMovs);
    if(JSON.stringify(normalized)!==JSON.stringify(stockMovs)){
      setStockMovs(normalized);
    }
  }, [normalizeStockMovementsCollection, stockMovs, setStockMovs]);

  useEffect(()=>{
    if(!selected?.id) return;
    const fresh = selected?._source === "occurrence"
      ? occurrences.find(o=>o.id===selected.id)
      : services.find(s=>s.id===selected.id);
    if(fresh){
      const next = selected?._source === "occurrence" ? { ...fresh, _source:"occurrence", type:"occurrence" } : fresh;
      if(JSON.stringify(next)!==JSON.stringify(selected)) setSelected(next);
    }
  }, [services, occurrences, selected]);

  useEffect(()=>{
    servicesRef.current = Array.isArray(services) ? services : [];
    occurrencesRef.current = Array.isArray(occurrences) ? occurrences : [];
  }, [services, occurrences]);

  useEffect(()=>{
    onClearOpenRecordRef.current = onClearOpenRecord;
  }, [onClearOpenRecord]);

  useEffect(()=>{
    selectedRef.current = selected;
  }, [selected]);

  const tryOpenPendingRecord = React.useCallback(()=>{
    const targetId = String(pendingOpenRecordIdRef.current || openRecordId || "").trim();
    if(!targetId) return false;
    if(consumedOpenRecordIdRef.current === targetId) return false;
    const currentSelected = selectedRef.current;
    const serviceMatch = (servicesRef.current || []).find(s=>String(s.id)===String(targetId) || String(s.clientId||"")===String(targetId));
    if(serviceMatch){
      if(!currentSelected || String(currentSelected.id)!==String(serviceMatch.id) || currentSelected?._source==="occurrence") setSelected(serviceMatch);
      pendingOpenRecordIdRef.current = null;
      consumedOpenRecordIdRef.current = targetId;
      if(onClearOpenRecordRef.current) onClearOpenRecordRef.current();
      return true;
    }
    const occMatch = (occurrencesRef.current || []).find(o=>String(o.id)===String(targetId) || String(o.clientId||"")===String(targetId));
    if(occMatch){
      const next = { ...occMatch, _source:"occurrence", type:"occurrence" };
      if(!currentSelected || String(currentSelected.id)!==String(next.id) || currentSelected?._source!=="occurrence") setSelected(next);
      pendingOpenRecordIdRef.current = null;
      consumedOpenRecordIdRef.current = targetId;
      if(onClearOpenRecordRef.current) onClearOpenRecordRef.current();
      return true;
    }
    return false;
  }, [openRecordId]);

  useEffect(()=>{
    if(!openRecordId) return;
    const normalizedOpenId = String(openRecordId).trim();
    if(consumedOpenRecordIdRef.current !== normalizedOpenId){
      consumedOpenRecordIdRef.current = null;
    }
    pendingOpenRecordIdRef.current = openRecordId;

    // v3.5.01: hybrid guard — mesmo padrão da v3.4.09/v3.4.10 de Compras.
    // PROBLEMA: requestPrioritySync imediato inicia pull_delta antes de qualquer
    // UDT operacional ser gerado pela abertura do detalhe via Live.
    // CAMINHO A (Live hidratou): fz:live-service-hydrated → esperar abertura
    //   do registro (~170ms) → disparar priority sync com delay 600ms.
    // CAMINHO B (pull_delta hidratou): fz:bg:updated/fz:priority-sync-done →
    //   tryOpenPendingRecord; já sem pull_delta concorrente.
    let liveHydratedThisRecord = false;
    let prioritySyncScheduled  = false;

    const schedulePrioritySync = (reason, delayMs = 0) => {
      if(prioritySyncScheduled) return;
      if(!currentFarmId || !syncCtx?.requestPrioritySync) return;
      prioritySyncScheduled = true;
      setTimeout(()=>{
        syncCtx.requestPrioritySync({
          collection: "services",
          farmId: currentFarmId,
          recordId: openRecordId,
          reason,
        }).catch(()=>{});
      }, delayMs);
    };

    const openedImmediately = tryOpenPendingRecord();
    if(!openedImmediately && !liveHydratedThisRecord) {
      // Registro não está na lista ainda — aguardar hidratação.
      // Não disparar requestPrioritySync agora; Live pode hidratar primeiro.
    }

    const done = (event)=>{
      const detail = event?.detail || {};
      if(detail?.collection && !["services","occurrences"].includes(detail.collection)) return;
      if(liveHydratedThisRecord) return;
      tryOpenPendingRecord();
    };

    const onLiveHydratedForSync = (event)=>{
      const detail = event?.detail || {};
      const targetId = String(detail?.recordId || detail?.record?.id || detail?.record?.clientId || "").trim();
      if(!targetId || targetId !== String(openRecordId || "").trim()) return;
      liveHydratedThisRecord = true;
      // Tentar abrir o registro imediatamente após hydration (~170ms).
      // Sem isso, a abertura ficaria aguardando o fallback de 4500ms.
      setTimeout(()=>{ tryOpenPendingRecord(); }, 170);
      schedulePrioritySync("live_hydrated_post_open", 600);
    };

    const timer = setTimeout(()=>{
      if(!tryOpenPendingRecord() && !liveHydratedThisRecord) {
        schedulePrioritySync("record_not_found_local", 0);
      } else {
        pendingOpenRecordIdRef.current = null;
      }
    }, 4500);

    window.addEventListener("fz:bg:updated", done);
    window.addEventListener("fz:priority-sync-done", done);
    window.addEventListener("fz:live-service-hydrated", onLiveHydratedForSync);
    return ()=>{
      window.removeEventListener("fz:bg:updated", done);
      window.removeEventListener("fz:priority-sync-done", done);
      window.removeEventListener("fz:live-service-hydrated", onLiveHydratedForSync);
      clearTimeout(timer);
    };
  }, [openRecordId, currentFarmId, syncCtx, tryOpenPendingRecord]);

  // ── Unified list ────────────────────────────────────────────────
  const occAsServices = occurrences.map(o=>({
    ...o, _source:"occurrence", type:"occurrence",
    title: o.title||o.description||"Ocorrência",
    priority: o.priority || (
              o.urgency==="Emergência"?"emergency":
              o.urgency==="Urgente"?"urgent":
              o.urgency==="Alta"?"high":"normal"),
  }));

  const canViewServiceInList = React.useCallback((svc)=>{
    if(!svc || isOperationalRecordDeleted(svc)) return false;
    if(isAdmin || isGestor) return true;
    const hasCostCenterAccess = itemMatchesCostCenterAccess(session, permissionModuleId, svc);
    if(session?.role === "colaborador") return isServiceParticipant(svc) || hasCostCenterAccess;
    return hasCostCenterAccess;
  }, [isAdmin, isGestor, session, permissionModuleId, isServiceParticipant]);
  const ownServiceFilter = React.useCallback((svc)=> canViewServiceInList(svc), [canViewServiceInList]);
  const ownOccurrenceFilter = React.useCallback((svc)=> !isOperationalRecordDeleted(svc) && canViewServiceInList(svc), [canViewServiceInList]);
  const svcsByTab = {
    internal:   services.filter(s=>normalizeServiceType(s)==="internal").filter(matchesBridgeCostCenter).filter(ownServiceFilter),
    occurrence: occAsServices.filter(matchesBridgeCostCenter).filter(ownOccurrenceFilter),
    external:   services.filter(s=>normalizeServiceType(s)==="external").filter(matchesBridgeCostCenter).filter(ownServiceFilter),
    providers:  [],
  };
  const scopedServices = services.filter(s=>!isOperationalRecordDeleted(s) && (mode==="external" ? normalizeServiceType(s)==="external" : normalizeServiceType(s)==="internal")).filter(matchesBridgeCostCenter).filter(ownServiceFilter);
  const currentInternalOpen = services.filter(s=>!isOperationalRecordDeleted(s) && normalizeServiceType(s)==="internal"&&! ["Concluído","Cancelado"].includes(s.status)).filter(matchesBridgeCostCenter).filter(ownServiceFilter).length;
  const currentExternalOpen = services.filter(s=>!isOperationalRecordDeleted(s) && normalizeServiceType(s)==="external"&&! ["Concluído","Cancelado","Negado"].includes(s.status)).filter(matchesBridgeCostCenter).filter(ownServiceFilter).length;
  const openServicesCount = scopedServices.filter(s=>!["Concluído","Cancelado","Negado"].includes(s.status)).length;
  const runningServicesCount = scopedServices.filter(s=>s.status==="Em Execução").length;
  const waitingServicesCount = scopedServices.filter(s=>["Aguardando Aprovação","Aprovado","Agendado","Aguardando Decisão","Pausada"].includes(s.status)).length;
  const concludedServicesCount = scopedServices.filter(s=>s.status==="Concluído").length;
  const moduleCardsConfig = (()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  })();
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.[permissionModuleId];
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig, permissionModuleId]);
  const currentSectionId = "moduleHome";
  const allowedServiceCards = React.useMemo(()=>getConfiguredSectionCards(currentSectionId, ["abertas","em_execucao","aguardando_decisao","concluidas"]), [currentSectionId, getConfiguredSectionCards]);
  const filterConfiguredCards = React.useCallback((items=[], allowedIds=[], fallbackIds=[])=>{
    const normalizedAllowed = Array.isArray(allowedIds) ? allowedIds.map(v=>String(v||"")) : [];
    const normalizedFallback = Array.isArray(fallbackIds) ? fallbackIds.map(v=>String(v||"")) : [];
    const normalizedItems = (Array.isArray(items) ? items : []).map((item, index)=>({
      ...item,
      id: String(item?.id || normalizedFallback[index] || `card_${index}`),
    }));
    if(!normalizedAllowed.length) return [];
    return normalizedItems.filter(item=>normalizedAllowed.includes(item.id));
  }, []);

  const visibleTabs = mode==="external"
    ? [
        ...(session?.role === "admin" && showProvidersTab !== false ? [{id:"providers", label:"Prestadores", icon:"🧑‍🔧", count:providerDirectory.length, color:"#14B8A6"}] : []),
        {id:"external", label:"Terceirizados", icon:"👷", count:currentExternalOpen, color:"#0D9488"}
      ]
    : [];
  useEffect(()=>{ if(tab==="providers" && !(session?.role === "admin" && showProvidersTab !== false)) setTab("external"); }, [tab, session?.role, showProvidersTab]);

  // v2.228.06: listas operacionais por última atividade real.
  // updatedAt vence quando houver edição; criação usa createdAt como fallback.
  const getServiceLastActivityMs = React.useCallback((record)=>{
    const candidates = [record?.updatedAt, record?.updated_at, record?.modifiedAt, record?.modified_at, record?.createdAt, record?.created_at, record?.reportedAt];
    for(const value of candidates){
      const ts = Date.parse(String(value || ""));
      if(Number.isFinite(ts)) return ts;
    }
    return 0;
  }, []);
  const compareServicesByLastActivity = React.useCallback((a,b)=>{
    const diff = getServiceLastActivityMs(b) - getServiceLastActivityMs(a);
    if(diff) return diff;
    return String(b?.id || b?.clientId || "").localeCompare(String(a?.id || a?.clientId || ""));
  }, [getServiceLastActivityMs]);

  const serviceTypeOptions = Array.from(new Set((svcsByTab[tab]||[]).map(s => s.title).filter(Boolean))).sort();
  const tabItems = (svcsByTab[tab]||[])
    .filter(s=>{
      if(cardFilter==="open") return !["Concluído","Cancelado","Negado"].includes(String(s?.status||""));
      if(cardFilter==="running") return s?.status==="Em Execução";
      if(cardFilter==="waiting") return ["Aguardando Aprovação","Aprovado","Agendado","Aguardando Decisão","Pausada"].includes(String(s?.status||""));
      if(cardFilter==="done") return String(s?.status||"")==="Concluído";
      return true;
    })
    .filter(s=>filterStatus==="Todos"||s.status===filterStatus)
    .filter(s=>filterType==="Todos"||s.title===filterType)
    .sort(compareServicesByLastActivity);

  // ── Save service ────────────────────────────────────────────────
  function saveService() {
    if(form?._new && !canCreate){show("Sem permissão para criar serviços.","error");return;}
    if(!form?._new && !canEditService){show("Sem permissão para editar serviços.","error");return;}
    if(!canViewCostCenter(session, permissionModuleId, form?.costCenter)){show("Sem acesso a este centro de custo.","error");return;}
    if(!canEditCostCenter(session, permissionModuleId, form?.costCenter)){show("Sem permissão para editar este centro de custo.","error");return;}
    if(!form.title?.trim()){show("Título obrigatório.","error");return;}
    const _ts=new Date().toISOString();
    const{_new,...d}=form;

    if(d.type==="occurrence"){
      const occPayload = {
        title:d.title,
        costCenter:d.costCenter || bridgeCostCenter || "Sede",
        category:d.category||"other",
        description:d.description||"",
        location:d.location||"",
        poiId:d.poiId||"",
        machineId:d.machineId||"",
        urgency:d.urgency||getPriorityConfig("occurrence", d.priority||"normal").label||"Normal",
        priority:d.priority||"normal",
        status:d.status||"Aberta",
        execType:d.execType||"",
        providerId:d.providerId||"",
        assignedTo:d.assignedTo||"",
        assignees:[...(d.assignees||[])],
        scheduledDate:d.scheduledDate||"",
        completionDate:d.completionDate||"",
        notes:d.notes||"",
        photos:d.photos||[],
        budget:d.budget||{ value:0, date:"", notes:"", photos:[] },
        reportedBy:d.reportedBy||myName,
        reportedAt:d.reportedAt||_ts,
        updatedBy:myName,
      };
      if(_new){
        const occClientId = createClientId("occ");
        const provisionalNumber = makeProvisionalRecordNumber(occurrences, { moduleKey:"occurrences", costCenter:occPayload.costCenter, costCenterCodeMap });
        const newOcc=markServiceCreatePending({
          ...occPayload,
          ...provisionalNumber,
          id:occClientId,
          clientId: occClientId,
          timeline:[makeTimelineEntry({action:"🔴 Ocorrência registrada",note:d.description||""}, _ts)],
          ...makeCreateMeta(myName),
        }, _ts);
        applyOccurrences(prev=>[...prev,newOcc]);
        appendServiceNotification(newOcc, {
          type:"occurrence_event",
          entityType:"occurrence",
          notificationModuleId:"occurrences",
          notificationSectionKey:"occurrences",
          event:"occurrence_created",
          eventLabel:"Ocorrência criada",
          title:"Nova ocorrência",
          summary:`${session?.name || myName || "Sistema"} criou a ocorrência ${newOcc.recordNumber || newOcc.id}${newOcc.costCenter ? ` · ${newOcc.costCenter}` : ""}.`,
          body:`${session?.name || myName || "Sistema"} criou a ocorrência ${newOcc.recordNumber || newOcc.id}${newOcc.costCenter ? ` · ${newOcc.costCenter}` : ""}.`,
          status:newOcc.status || "Aberta",
          eventAt:_ts,
          entityLabel:"a ocorrência",
        });
      } else {
        const prevOcc = occurrences.find(o=>o.id===d.id);
        const tl=[...(d.timeline||prevOcc?.timeline||[])];
        if(prevOcc?.status!==occPayload.status) tl.push(makeTimelineEntry({action:`Status: ${prevOcc?.status||"-"} → ${occPayload.status}`,note:""}, _ts));
        let nextOcc = null;
        applyOccurrences(prev=>prev.map(o=>{
          if(o.id!==d.id) return o;
          nextOcc = markServiceUpdatePending({...o,...occPayload,id:String(d.id||o.id||createClientId("occ")),clientId:String(d.clientId||o.clientId||d.id||o.id||createClientId("occ")),timeline:tl,...makeUpdateMeta(myName)}, _ts, "occurrence_update");
          return nextOcc;
        }));
        if(prevOcc && nextOcc){
          appendServiceNotification(nextOcc, {
            type:"occurrence_event",
            entityType:"occurrence",
            notificationModuleId:"occurrences",
            notificationSectionKey:"occurrences",
            event:"occurrence_status_changed",
            eventLabel:"Status da ocorrência atualizado",
            title:`Ocorrência ${nextOcc.status || "atualizada"}`,
            summary:`${session?.name || myName || "Sistema"} atualizou a ocorrência ${nextOcc.recordNumber || nextOcc.id} de ${prevOcc?.status || "sem status"} para ${nextOcc?.status || "sem status"}.`,
            body:`${session?.name || myName || "Sistema"} atualizou a ocorrência ${nextOcc.recordNumber || nextOcc.id} de ${prevOcc?.status || "sem status"} para ${nextOcc?.status || "sem status"}.`,
            status:nextOcc.status || "Aberta",
            previousStatus:prevOcc?.status || "",
            eventAt:_ts,
            entityLabel:"a ocorrência",
          });
        }
      }
      show("✅ Ocorrência salva!");
      setForm(null);
      syncOnSave("occurrences");
      return;
    }

    if(_new){
      const initialStatus = d.status||"Aberto";
      const maintenancePatch = buildServiceMaintenancePatch(d, { status: initialStatus, startedAt: d.startedAt, completedAt: d.completedAt, dateEnd: d.dateEnd });
      const servicePrefix = normalizeServiceType(d)==="external" ? "extsvc" : "svc";
      const svcClientId = createClientId(servicePrefix);
      const moduleKey = normalizeServiceType(d)==="external" ? "externalServices" : "services";
      const provisionalNumber = makeProvisionalRecordNumber(services, { moduleKey, costCenter:d.costCenter || bridgeCostCenter || "Sede", costCenterCodeMap });
      const newSvc=markServiceCreatePending(stampRecordGeneration({...d,costCenter:d.costCenter || bridgeCostCenter || "Sede",id:svcClientId,clientId:svcClientId,
        ...provisionalNumber,
        status:initialStatus,
        ...maintenancePatch,
        ...makeCreateMeta(myName),
        nfPending:!!d.nfPending,
        timeline:[makeTimelineEntry({action:"Serviço registrado",note:d.description||""}, _ts)]}, currentFarm), _ts);
      applyServices(prev=>[...prev,newSvc]);
      createServiceNotifications(newSvc);
    } else {
      const prevSvc = services.find(s=>s.id===d.id) || {};
      const tl=[...(d.timeline||prevSvc.timeline||[])];
      if((prevSvc.status||"") !== (d.status||"")){
        tl.push(makeTimelineEntry({action:`Status: ${prevSvc.status||"-"} → ${d.status||"-"}`,note:"Status alterado na reabertura/edição"}, _ts));
      }
      if(prevSvc.nfPending && d.nfNum){
        tl.push(makeTimelineEntry({action:"Nota fiscal anexada após conclusão",note:`NF: ${d.nfNum}`}, _ts));
      }
      let nextSvc = null;
      applyServices(prev=>prev.map(s=>{
        if(s.id!==d.id) return s;
        const maintenancePatch = buildServiceMaintenancePatch(s, { ...d, status: d.status, startedAt: d.startedAt, completedAt: d.completedAt, dateEnd: d.dateEnd });
        nextSvc = markServiceUpdatePending(stampRecordGeneration({...s,...d,id:String(d.id || s.id || createClientId(normalizeServiceType(s)==="external" ? "extsvc" : "svc")),clientId:String(d.clientId || s.clientId || d.id || s.id || createClientId(normalizeServiceType(s)==="external" ? "extsvc" : "svc")),costCenter:d.costCenter || bridgeCostCenter || s.costCenter || "Sede",...maintenancePatch,timeline:tl,...makeUpdateMeta(myName),nfPending:!!(d.nfPending && !d.nfNum)}, currentFarm), _ts, "service_update");
        return nextSvc;
      }));
      if(prevSvc && nextSvc){
        notifyServiceStatusChange(prevSvc, nextSvc, { eventAt:_ts });
      }
    }
    show("✅ Salvo!"); setForm(null); syncOnSave("services");
  }


  // ── Use materials (stock out) ───────────────────────────────────
  function saveMaterialsForService(serviceId, items) {
    if(!serviceId){ show("Serviço inválido.","error"); return; }
    const valid = (items||[]).map(i=>({
      ...i,
      qtySeparated:Number(i.qtySeparated||0),
      qtyUsed:(i.qtyUsed===undefined || i.qtyUsed===null || String(i.qtyUsed).trim()==="") ? null : Number(i.qtyUsed),
    })).filter(i=>i.stockId && i.qtySeparated>0);
    if(!valid.length){show("Adicione ao menos 1 item.","error");return;}
    const _ts=new Date().toISOString();
    const svc=services.find(s=>s.id===serviceId);
    const newMovs=[];
    const materials=[];
    for (const i of valid) {
      const item=enrichedStockItems.find(s=>String(s.id)===String(i.stockId));
      if(!item) continue;
      if(i.qtyUsed!=null && i.qtyUsed > i.qtySeparated){show(`A quantidade utilizada não pode ser maior que a separada em ${item.name}.`,`error`);return;}
      if(Number(item.quantity||0) < i.qtySeparated){show(`⚠️ Estoque insuficiente para ${item.name}. Disponível: ${item.quantity||0} ${item.unit||""}`, "error");return;}
      const materialEntry = makeServiceMaterialEntry({
        stockId:i.stockId,name:item.name,unit:item.unit||"un",qtySeparated:i.qtySeparated,qtyUsed:i.qtyUsed,
        qty:(i.qtyUsed==null?i.qtySeparated:i.qtyUsed),pendingUsage:i.qtyUsed==null,
        costCenter:item?.costCenter||"Sede",
      }, _ts);
      newMovs.push(makeStockMovementEntry({
        itemId:i.stockId,type:"saida",qty:i.qtySeparated,
        notes:`Serviço: ${svc?.title||("#"+serviceId)} · material separado`,
        costCenter:item?.costCenter||"Sede",
        origin:"service_material", originId:serviceId, originMaterialClientId:materialEntry.clientId, farmId: currentFarmId || svc?.farmId || ""
      }, _ts));
      if(i.qtyUsed!=null && i.qtySeparated > i.qtyUsed){
        newMovs.push(makeStockMovementEntry({
          itemId:i.stockId,type:"entrada",qty:(i.qtySeparated-i.qtyUsed),
          notes:`Serviço: ${svc?.title||("#"+serviceId)} · devolução de material`,
          costCenter:item?.costCenter||"Sede",
          origin:"service_material_return", originId:serviceId, originMaterialClientId:materialEntry.clientId, farmId: currentFarmId || svc?.farmId || ""
        }, _ts));
      }
      materials.push(materialEntry);
    }
    if(!materials.length){show("Nenhum material válido selecionado.","error");return;}
    const historyNote = materials.map(m=>`${m.name}: sep. ${m.qtySeparated} / util. ${m.pendingUsage?"pendente":m.qtyUsed} ${m.unit}`).join(", ");
    const historyEntry = makeTimelineEntry({ action:"Materiais adicionados", note:historyNote }, _ts);
    let updatedService = null;
    applyServices(prev=>prev.map(s=>{
      if(s.id!==serviceId) return s;
      updatedService = markServiceUpdatePending({
        ...s,
        materials:[...(s.materials||[]), ...materials],
        timeline:[...(s.timeline||[]), historyEntry],
        ...makeUpdateMeta(myName),
      }, _ts, "service_materials");
      return updatedService;
    }));
    if(updatedService && selected?.id===serviceId) setSelected(updatedService);
    if(updatedService && form?.id===serviceId) setForm(updatedService);
    applyStockMovements(prev=>[...prev,...newMovs]);
    syncOnSave("services");
    syncOnSave("stockMovements");
    show(materials.some(m=>m.pendingUsage)?"✅ Materiais separados. Depois informe a quantidade utilizada; se não informar, valerá a quantidade separada.":"✅ Materiais registrados e estoque atualizado em tempo real!"); setMatForm(null);
  }

  function saveServiceMaterialUsage(serviceId, materialIndex, qtyUsedValue) {
    const qtyUsed = Number(qtyUsedValue);
    if(!Number.isFinite(qtyUsed) || qtyUsed < 0){ show("Informe a quantidade utilizada.","error"); return; }
    const svc = services.find(s=>s.id===serviceId) || selected;
    const material = svc?.materials?.[materialIndex];
    if(!svc || !material){ show("Material não encontrado.","error"); return; }
    const qtySeparated = Number(material.qtySeparated || material.qty || 0);
    if(qtyUsed > qtySeparated){ show("A quantidade utilizada não pode ser maior que a separada.","error"); return; }
    const previouslyAccounted = Number(material.qtyUsed ?? material.qty ?? qtySeparated);
    const stockItem = enrichedStockItems.find(s=>String(s.id)===String(material.stockId));
    const _ts = new Date().toISOString();
    const newMovs = [];
    if(qtyUsed < previouslyAccounted){
      newMovs.push(makeStockMovementEntry({
        itemId:material.stockId, type:"entrada", qty:(previouslyAccounted-qtyUsed),
        notes:`Serviço: ${svc?.title||("#"+serviceId)} · ajuste da quantidade utilizada`,
        costCenter:stockItem?.costCenter||svc?.costCenter||"Sede",
        origin:"service_material_usage_adjust", originId:serviceId, originMaterialClientId:material.clientId || material.id || "", farmId: currentFarmId || svc?.farmId || "",
      }, _ts));
    } else if(qtyUsed > previouslyAccounted){
      newMovs.push(makeStockMovementEntry({
        itemId:material.stockId, type:"saida", qty:(qtyUsed-previouslyAccounted),
        notes:`Serviço: ${svc?.title||("#"+serviceId)} · ajuste adicional da quantidade utilizada`,
        costCenter:stockItem?.costCenter||svc?.costCenter||"Sede",
        origin:"service_material_usage_adjust", originId:serviceId, originMaterialClientId:material.clientId || material.id || "", farmId: currentFarmId || svc?.farmId || "",
      }, _ts));
    }
    const note = `${material.name||stockItem?.name||material.stockId}: utilizado ${qtyUsed} ${material.unit||stockItem?.unit||"un"}${qtyUsed<qtySeparated?` (retorno ${qtySeparated-qtyUsed})`:""}`;
    const timelineEntry = makeTimelineEntry({ action:"Quantidade utilizada informada", note }, _ts);
    let updatedService = null;
    applyServices(prev=>prev.map(s=>{
      if(s.id!==serviceId) return s;
      const nextMaterials = (s.materials||[]).map((m,idx)=> idx!==materialIndex ? m : makeServiceMaterialEntry({ ...m, qtyUsed, qty:qtyUsed, pendingUsage:false, usedUpdatedAt:_ts, usedUpdatedBy:myName }, _ts));
      updatedService = markServiceUpdatePending({ ...s, materials:nextMaterials, timeline:[...(s.timeline||[]), timelineEntry], ...makeUpdateMeta(myName) }, _ts, "service_material_usage");
      return updatedService;
    }));
    if(updatedService && selected?.id===serviceId) setSelected(updatedService);
    if(updatedService && form?.id===serviceId) setForm(updatedService);
    if(newMovs.length){ applyStockMovements(prev=>[...prev,...newMovs]); syncOnSave("stockMovements"); }
    syncOnSave("services");
    setServiceMaterialUsageDrafts(prev=>{ const next={...prev}; delete next[`${serviceId}::${materialIndex}`]; return next; });
    show("✅ Quantidade utilizada atualizada.");
  }
  function confirmServiceMaterialRestock(svc, note="") {
    const latestSvc = services.find(s=>s.id===svc?.id) || occurrences.find(o=>o.id===svc?.id) || svc;
    if(hasCompletedMaterialRestock(latestSvc)){
      setSvcRestockForm(null);
      show("Este registro já teve os materiais estornados.","info");
      return;
    }
    const materials = getRestockableMaterials(latestSvc);
    if(!materials.length){
      show("Este registro não possui materiais para estorno.","info");
      setSvcRestockForm(null);
      return;
    }
    const _ts = new Date().toISOString();
    const isOccRecord = latestSvc?.type==="occurrence" || latestSvc?._source==="occurrence";
    const originType = isOccRecord ? "occurrence_delete_restock" : "service_delete_restock";
    const labelType = isOccRecord ? "Ocorrência" : (normalizeServiceType(latestSvc)==="external"?"Serviço terceirizado":"Serviço");
    const movements = materials.map((m)=>makeStockMovementEntry({
      itemId:m.stockId,
      type:"entrada",
      qty:getMaterialUsageQty(m),
      notes:`${labelType}: ${latestSvc?.title||("#"+latestSvc?.id)} · estorno para exclusão${note?` · ${note}`:""}`,
      costCenter:latestSvc?.costCenter || m?.costCenter || "Sede",
      origin:originType,
      originId:latestSvc?.id,
      originMaterialClientId:m?.clientId || m?.id || "",
      farmId: currentFarmId || latestSvc?.farmId || "",
    }, _ts));
    applyStockMovements(prev=>[...prev, ...movements]);
    if(isOccRecord){
      applyOccurrences(prev=>prev.map(o=>o.id!==latestSvc.id?o:markServiceUpdatePending({
        ...o,
        materialRestockCompletedAt:_ts,
        materialRestockCompletedBy:myName,
        materialRestockMovementIds:movements.map(m=>m.id),
        timeline:[...(o.timeline||[]),makeTimelineEntry({action:"♻️ Estorno de materiais",note:note || "Materiais devolvidos ao estoque para habilitar exclusão"}, _ts)],
        ...makeUpdateMeta(myName),
      }, _ts, "occurrence_material_restock")));
      syncOnSave("occurrences");
    } else {
      updateSvc(latestSvc.id, {
        materialRestockCompletedAt:_ts,
        materialRestockCompletedBy:myName,
        materialRestockMovementIds:movements.map(m=>m.id),
      }, {action:"♻️ Estorno de materiais", note: note || "Materiais devolvidos ao estoque para habilitar exclusão"});
    }
    syncOnSave("stockMovements");
    setSvcRestockForm(null);
    show("✅ Estorno realizado. Agora a exclusão do registro está habilitada.");
  }
  function requestServiceDelete(svc) {
    const latestSvc = services.find(s=>s.id===svc?.id) || occurrences.find(o=>o.id===svc?.id) || svc;
    if(hasPendingMaterialRestock(latestSvc)){
      setSvcRestockForm(latestSvc);
      return;
    }
    const _ts=new Date().toISOString();
    if(latestSvc?.type==="occurrence" || latestSvc?._source==="occurrence"){
      applyOccurrences(prev=>prev.map(o=>o.id!==latestSvc.id?o:markServiceUpdatePending({...o,_deleted:true,deletedAt:_ts,deleted_at:_ts,deletedBy:myName,status:"Cancelada",timeline:[...(o.timeline||[]),makeTimelineEntry({action:"🗑️ Ocorrência excluída",note:"Registro removido"}, _ts)],...makeUpdateMeta(myName,{ deletedAt:_ts, deleted_at:_ts, deletedBy:myName })}, _ts, "occurrence_delete")));
      syncOnSave("occurrences");
      setForm(null);
      show("🗑️ Removido.");
      return;
    }
    applyServices(prev=>prev.map(s=>s.id!==latestSvc.id?s:markServiceUpdatePending({...s,_deleted:true,deletedAt:_ts,deleted_at:_ts,deletedBy:myName,status:"Cancelada",timeline:[...(s.timeline||[]),makeTimelineEntry({action:"🗑️ Registro excluído",note:"Registro removido"}, _ts)],...makeUpdateMeta(myName,{ deletedAt:_ts, deleted_at:_ts, deletedBy:myName })}, _ts, "service_delete")));
    syncOnSave("services");
    setForm(null);
    show("🗑️ Removido.");
  }

  // ── Budget submit ───────────────────────────────────────────────
  function saveBudget() {
    if(!canEditService){show("Sem permissão para editar serviços.","error");return;}
    if(!budgetForm.provider){show("Selecione o prestador.","error");return;}
    if(!budgetForm.value){show("Informe o valor.","error");return;}
    updateSvc(budgetForm.svcId,{
      status:"Aguardando Aprovação",
      budget:{provider:budgetForm.provider,value:Number(budgetForm.value),
              partsCost:Number(budgetForm.partsCost||0),obs:budgetForm.obs||"",
              payMethod:budgetForm.payMethod||"",
              submittedAt:new Date().toISOString(),submittedBy:myName},
    },{action:"Orçamento recebido",note:`${budgetForm.provider} — R$ ${fmtMoney(budgetForm.value)}`});
    show("✅ Orçamento registrado!"); setBudgetForm(null);
  }

  function approveBudget(id){
    if(!canApprove)return;
    const svc = services.find(s=>s.id===id);
    updateSvc(id,{status:"Aprovado",approvedBy:myName,approvedAt:new Date().toISOString()},{action:"Orçamento aprovado"});
    if(svc?.budget?.value){
      appendFinancialEntry({ originId:`${id}:budget:${svc?.approvedAt||new Date().toISOString()}`, description:`Previsão aprovada · ${svc.title||id}`, amount:Number(svc.budget.value||0), costCenter:svc.costCenter||svc.category||'Sede', paymentMethod:svc.budget.payMethod||'', status:'Previsto', notes:`Prestador: ${svc.budget.provider||''} · Forma: ${svc.budget.payMethod||''}`, metadata:{ stage:'budget_approved', provider:svc.budget.provider||'', serviceId:id } });
    }
    show("✅ Aprovado!");
  }
  function denyBudget(id,reason){
    if(!canApprove)return;
    updateSvc(id,{status:"Negado",deniedBy:myName,denyReason:reason,deniedAt:new Date().toISOString()},{action:"Orçamento negado",note:reason});
    show("❌ Negado."); setDenyForm(null);
  }

  // ── Schedule ────────────────────────────────────────────────────
  function saveSchedule(){
    if(!canEditService){show("Sem permissão para editar serviços.","error");return;}
    if(!schedForm.execDate){show("Informe a data de execução.","error");return;}
    const svc=services.find(s=>s.id===schedForm.svcId);
    if(svc?.execDate&&!isAdmin){show("Apenas admin pode alterar datas já definidas.","error");return;}
    updateSvc(schedForm.svcId,{execDate:schedForm.execDate,deliveryDate:schedForm.deliveryDate||"",
      status:svc?.status==="Aprovado"?"Aprovado":(svc?.status==="Agendado"?"Agendado":"Agendado")},
      {action:"Datas definidas",note:`Execução: ${schedForm.execDate}${schedForm.deliveryDate?` · Entrega: ${schedForm.deliveryDate}`:""}`});
    show("✅ Datas salvas!"); setSchedForm(null);
  }

  // ── NF on completion ────────────────────────────────────────────

  // ── Helpers ─────────────────────────────────────────────────────
  const StBadge=({status})=>{
    const sc=SVC_STATUS_FLOW[status]||{color:"#64748B",icon:"⚪"};
    return<span style={{background:`${sc.color}22`,color:sc.color,border:`1px solid ${sc.color}44`,
      padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:700,whiteSpace:"nowrap"}}>{sc.icon} {status}</span>;
  };
  const TyBadge=({type})=>{
    const tc=SVC_TYPES[type]||SVC_TYPES.internal;
    return<span style={{background:`${tc.color}22`,color:tc.color,fontSize:10,fontWeight:700,
      padding:"1px 7px",borderRadius:10}}>{tc.icon} {tc.label}</span>;
  };
  const PrBadge=({type,priority})=>{
    const p=getPriorityConfig(type,priority);
    return<span style={{fontSize:10,color:p.color,fontWeight:700}}>{p.icon} {p.label}</span>;
  };

  const defForm=(type)=> type==="occurrence"
    ? ({_new:true,type,title:"",description:"",priority:"normal",category:"other",urgency:"Normal",
        status:"Aberta",date:today(),location:"",poiId:"",machineId:"",assignees:[],assignedTo:"",
        providersList:[],providerId:"",contactPerson:"",execType:"",payMethod:"",notes:"",costCenter:bridgeCostCenter || allowedCostCenters[0] || "Sede",
        scheduledDate:"",completionDate:"",photos:[],budget:{ value:0, date:"", notes:"", photos:[] },
        reportedBy:myName, reportedAt:new Date().toISOString()})
    : ({_new:true,type,title:"",description:"",priority:"normal",category:"other",
        status:"Aberto",date:today(),location:"",poiId:"",machineId:"",costCenter:bridgeCostCenter || allowedCostCenters[0] || "Sede",
        assignees:[],providers:[],contactPerson:"",execType:"",payMethod:"",notes:"",servicePhotos:[]});

  const canEditItem=(item)=>{
    if(!canEditService) return false;
    const closed=["Concluído","Cancelado","Negado","Concluída","Cancelada"].includes(item.status);
    if(closed) return isAdmin || canEditService;
    return true;
  };

  // ── Filtered counts for header ──────────────────────────────────

  return(
    <div>
      <Toast toast={toast}/>

      {/* ── INLINE MATERIALS / RESTOCK ───────────────────────────── */}
      {matForm&&<div style={{marginBottom:16}}><button onClick={()=>setMatForm(null)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button><div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}><div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14}}>📦 Adicionar Materiais</div><div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{background:`${T.yellow}15`,border:`1px solid ${T.yellow}44`,borderRadius:8,padding:"10px 12px",fontSize:12,color:T.yellow,lineHeight:1.5}}>
          ⚠️ A quantidade separada sai do estoque imediatamente. A quantidade utilizada pode ser informada depois. Se ela não for informada, a baixa considerada será a quantidade separada.
        </div>
        {(matForm.items||[]).map((item,i)=>{ const selected=(enrichedStockItems||[]).find(s=>String(s.id)===String(item.stockId)); return(
          <div key={i} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12,display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1.4fr 110px 110px",gap:8,alignItems:"flex-end"}}>
              <Field label="Produto">
                <Select value={item.stockId||""} onChange={e=>setMatForm(f=>({...f,items:(f.items||[]).map((x,j)=>j===i?{...x,stockId:e.target.value}:x)}))}>
                  <option value="">Selecione...</option>
                  {(enrichedStockItems||[]).map(s=><option key={s.id} value={s.id}>{s.name} (saldo: {s.quantity||0} {s.unit})</option>)}
                </Select>
              </Field>
              <Field label="Separada">
                <Input type="number" min="0.001" step="0.001" value={item.qtySeparated||""} onChange={e=>setMatForm(f=>({...f,items:(f.items||[]).map((x,j)=>j===i?{...x,qtySeparated:e.target.value}:x)}))}/>
              </Field>
              <Field label="Utilizada (pode informar depois)">
                <Input type="number" min="0" step="0.001" value={item.qtyUsed||""} onChange={e=>setMatForm(f=>({...f,items:(f.items||[]).map((x,j)=>j===i?{...x,qtyUsed:e.target.value}:x)}))} placeholder={selected?.unit||""}/>
              </Field>
            </div>
            <button onClick={()=>setMatForm(f=>({...f,items:(f.items||[]).length===1?(f.items||[]):(f.items||[]).filter((_,j)=>j!==i)}))} style={{alignSelf:"stretch",background:`${T.red}18`,border:`1px solid ${T.red}33`,color:T.red,cursor:"pointer",fontSize:13,fontWeight:700,borderRadius:10,padding:"10px 12px"}}>Excluir item</button>
          </div>
        );})}
        <Btn size="sm" variant="ghost" onClick={()=>setMatForm(f=>({...f,items:[...(f.items||[]),{stockId:"",qtySeparated:"",qtyUsed:""}]}))}>+ Item</Btn>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Btn variant="ghost" onClick={()=>setMatForm(null)}>Cancelar</Btn>
          <Btn onClick={()=>saveMaterialsForService(matForm.svcId, matForm.items||[])}>📦 Confirmar</Btn>
        </div>
      </div></div></div>}
      {svcRestockForm&&<div style={{marginBottom:16}}><button onClick={()=>setSvcRestockForm(null)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button><div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}><div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14}}>♻️ Estorno de Materiais para Estoque</div><div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{fontSize:13,color:T.text,fontWeight:700}}>{svcRestockForm.title||"Registro"}</div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.45}}>Para excluir este registro, é necessário devolver ao estoque todos os materiais consumidos. Confira abaixo e confirme o estorno para habilitar a exclusão.</div>
        <div style={{display:"grid",gap:8}}>
          {getRestockableMaterials(svcRestockForm).map((m,idx)=><div key={`${m.stockId||'item'}_${idx}`} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:10,alignItems:"center",padding:"10px 12px",background:T.card2,border:`1px solid ${T.border}`,borderRadius:10}}><div><div style={{fontSize:13,fontWeight:700,color:T.text}}>{m.name||m.stockId}</div><div style={{fontSize:11,color:T.muted}}>Retorno integral ao estoque</div></div><div style={{fontSize:13,fontWeight:800,color:T.accent,whiteSpace:"nowrap"}}>{getMaterialUsageQty(m)} {m.unit||"un"}</div></div>)}
        </div>
        <Field label="Observação do estorno">
          <Textarea rows={2} value={svcRestockForm.note||""} onChange={e=>setSvcRestockForm(f=>({...f,note:e.target.value}))} placeholder="Opcional. Ex.: exclusão do registro após devolução dos materiais."/>
        </Field>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,paddingTop:6,borderTop:`1px solid ${T.border}`}}>
          <div style={{fontSize:12,color:T.muted}}>Total a devolver: <strong style={{color:T.text}}>{getRestockableMaterials(svcRestockForm).reduce((sum,m)=>sum+getMaterialUsageQty(m),0)}</strong></div>
          <div style={{display:"flex",gap:10}}>
            <Btn variant="ghost" onClick={()=>setSvcRestockForm(null)}>Cancelar</Btn>
            <Btn onClick={()=>confirmServiceMaterialRestock(svcRestockForm, svcRestockForm.note||"")}>Confirmar estorno</Btn>
          </div>
        </div>
      </div></div></div>}

      {/* ── INLINE BUDGET / SCHEDULE ─────────────────────────────── */}
      {budgetForm&&<div style={{marginBottom:16}}><button onClick={()=>setBudgetForm(null)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button><div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}><div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14}}>💰 Registrar Orçamento</div><div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Field label="Prestador *">
            <Select value={budgetForm.provider||""} onChange={e=>setBudgetForm(f=>({...f,provider:e.target.value}))}>
              <option value="">Selecione...</option>
              {providerDirectory.map(p=><option key={p.id} value={p.name}>{p.name} — {p.specialty || p.serviceCategories?.[0] || "Prestador"}</option>)}
            </Select>
          </Field>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:12}}>
            <Field label="Valor do Serviço (R$)">
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:13}}>R$</span>
                <Input type="number" min="0" step="0.01" value={budgetForm.value||""} onChange={e=>setBudgetForm(f=>({...f,value:e.target.value}))} style={{paddingLeft:30}}/>
              </div>
            </Field>
            <Field label="Valor das Peças (R$)">
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:13}}>R$</span>
                <Input type="number" min="0" step="0.01" value={budgetForm.partsCost||""} onChange={e=>setBudgetForm(f=>({...f,partsCost:e.target.value}))} style={{paddingLeft:30}}/>
              </div>
            </Field>
          </div>
          <Field label="Forma de Pagamento">
            <Select value={budgetForm.payMethod||""} onChange={e=>setBudgetForm(f=>({...f,payMethod:e.target.value}))}>
              <option value="">Selecione...</option>
              {(SL.SERVICE_PAY_METHODS||["À Vista","PIX","Boleto","Parcelado"]).map(m=><option key={m}>{m}</option>)}
            </Select>
          </Field>
          <Field label="Observações">
            <Textarea rows={2} value={budgetForm.obs||""} onChange={e=>setBudgetForm(f=>({...f,obs:e.target.value}))} placeholder="Prazo, condições..."/>
          </Field>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setBudgetForm(null)}>Cancelar</Btn>
            <Btn onClick={saveBudget}>💰 Registrar</Btn>
          </div>
        </div></div></div>}

      {schedForm&&<div style={{marginBottom:16}}><button onClick={()=>setSchedForm(null)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button><div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}><div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14}}>📅 Agendar Execução</div><div style={{display:"flex",flexDirection:"column",gap:14}}>
          {services.find(s=>s.id===schedForm.svcId)?.execDate&&!isAdmin&&
            <div style={{background:`${T.yellow}15`,border:`1px solid ${T.yellow}44`,borderRadius:8,padding:"8px 12px",fontSize:12,color:T.yellow}}>
              ⚠️ Datas já definidas. Apenas admin pode alterar.
            </div>}
          <Field label="Data de Execução *"><Input type="date" value={schedForm.execDate||""} onChange={e=>setSchedForm(f=>({...f,execDate:e.target.value}))}/></Field>
          <Field label="Data de Entrega / Conclusão"><Input type="date" value={schedForm.deliveryDate||""} onChange={e=>setSchedForm(f=>({...f,deliveryDate:e.target.value}))}/></Field>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setSchedForm(null)}>Cancelar</Btn>
            <Btn onClick={saveSchedule}>📅 Salvar</Btn>
          </div>
        </div></div></div>}

      {/* ── INLINE NF ───────────────────────────────────────────── */}
      {nfForm&&<div style={{marginBottom:16}}><button onClick={()=>setNfForm(null)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button><div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}><div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14}}>📄 Concluir com Nota Fiscal</div><div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Field label="Número da NF">
          <Input value={nfForm.nfNum||""} onChange={e=>setNfForm(f=>({...f,nfNum:e.target.value}))} placeholder="Ex: 001234"/>
        </Field>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:12}}>
          <Field label="Valor final do serviço (R$)">
            <Input type="number" min="0" step="0.01" value={nfForm.finalValue||""} onChange={e=>setNfForm(f=>({...f,finalValue:e.target.value}))} placeholder="Opcional"/>
          </Field>
          <Field label="Vencimento / pagamento">
            <Input type="date" value={nfForm.dueDate||today()} onChange={e=>setNfForm(f=>({...f,dueDate:e.target.value}))}/>
          </Field>
          <Field label="Forma de pagamento">
            <Select value={nfForm.paymentMethod||""} onChange={e=>setNfForm(f=>({...f,paymentMethod:e.target.value}))}>
              <option value="">Selecione...</option>
              {["PIX","Boleto","Dinheiro","Cartão","Transferência","Prazo","Outro"].map(opt=><option key={opt}>{opt}</option>)}
            </Select>
          </Field>
          <Field label="Justificativa se faltar valor/NF">
            <Input value={nfForm.justificationMissing||""} onChange={e=>setNfForm(f=>({...f,justificationMissing:e.target.value}))} placeholder="Explique ausência de informação"/>
          </Field>
        </div>
        <Field label="Foto da NF">
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            {nfForm.nfPhoto&&<img src={nfForm.nfPhoto} alt="" style={{width:60,height:60,objectFit:"cover",borderRadius:8}}/>}
            <button type="button" onClick={pickInlineNfPhoto}
              style={{padding:"8px 16px",borderRadius:8,border:`1px dashed ${T.border}`,background:T.card2,cursor:"pointer",color:T.muted,fontSize:13}}>
              📷 {nfForm.nfPhoto?"Trocar":"Adicionar"}
            </button>
          </div>
        </Field>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Btn variant="ghost" onClick={()=>setNfForm(null)}>Cancelar</Btn>
          <Btn onClick={saveInlineNf}>✅ Concluir Serviço</Btn>
        </div>
      </div></div></div>}

      {/* ── DENY MODAL ───────────────────────────────────────────── */}
      {denyForm&&<ServiceDenyModal
        denyReasons={SL.SERVICE_DENY_REASONS||["Orçamento acima do esperado","Serviço não prioritário","Outro motivo"]}
        onClose={()=>setDenyForm(null)}
        onConfirm={reason=>denyBudget(denyForm.svcId,reason)}
      />}

      {pauseForm&&<div style={{marginBottom:16}}><button onClick={()=>setPauseForm(null)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button><div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}><div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14}}>⏸️ Pausar Tarefa</div><div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Field label="Justificativa *">
          <Select value={pauseForm.reason||""} onChange={e=>setPauseForm(f=>({...f,reason:e.target.value}))}>
            {(pauseReasonOptions||[]).map(r=><option key={r}>{r}</option>)}
          </Select>
        </Field>
        <Field label="Observação">
          <Textarea rows={3} value={pauseForm.obs||""} onChange={e=>setPauseForm(f=>({...f,obs:e.target.value}))} placeholder="Detalhes adicionais (opcional)"/>
        </Field>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
          <Btn variant="ghost" onClick={()=>setPauseForm(null)}>Cancelar</Btn>
          <Btn onClick={()=>{ if(!(pauseForm.reason||"").trim()){ show("Selecione a justificativa da pausa.","error"); return; } updateSvc(pauseForm.svcId,{status:"Pausada",pausedAt:new Date().toISOString(),pauseReason:pauseForm.reason,pauseNote:pauseForm.obs||""},{action:"Tarefa pausada",note:`${pauseForm.reason}${pauseForm.obs?` — ${pauseForm.obs}`:""}`}); setPauseForm(null); }}>Salvar Pausa</Btn>
        </div>
      </div></div></div>}

      {svcPhotoForm&&<div style={{marginBottom:16}}><button onClick={()=>setSvcPhotoForm(null)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button><div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}><div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14,display:"inline-flex",alignItems:"center",gap:8}}><ServicePhotoAddPhoneIcon size={18}/> Adicionar Fotos</div><div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{fontSize:12,color:T.muted}}>Selecione um título obrigatório para cada foto. As fotos ficam armazenadas no banco de dados dentro do serviço, com compressão automática. Limite: 10 fotos por serviço.</div>
        <div style={{fontSize:12,color:T.muted,display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
          <span>Já cadastradas: <strong style={{color:T.text}}>{getServicePhotoEntries(services.find(s=>s.id===svcPhotoForm.svcId)||{}).length}</strong></span>
          <span>Você pode adicionar agora: <strong style={{color:T.text}}>{Math.max(0, 10 - getServicePhotoEntries(services.find(s=>s.id===svcPhotoForm.svcId)||{}).length)}</strong></span>
        </div>
        {(svcPhotoForm.rows||[]).map((row,idx)=><div key={row.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 210px auto",gap:10,alignItems:"end",padding:"10px 12px",background:T.card2,border:`1px solid ${T.border}`,borderRadius:10}}>
          <Field label={`Título ${idx+1} *`}>
            <Select value={row.title} onChange={e=>setSvcPhotoForm(prev=>({...prev,rows:(prev.rows||[]).map(x=>x.id===row.id?{...x,title:e.target.value}:x)}))}>
              <option value="">Selecione...</option>
              {(servicePhotoTitleOptions||[]).map(opt=><option key={opt}>{opt}</option>)}
            </Select>
          </Field>
          <Field label="Foto *">
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              {row.thumb&&<img src={row.thumb} alt="" style={{width:60,height:60,objectFit:"cover",borderRadius:10,border:`1px solid ${T.border}`,boxShadow:"0 6px 16px rgba(0,0,0,.18)"}}/>}
              <button type="button" onClick={()=>pickInlineServicePhoto(row.id)} style={{padding:"8px 14px",borderRadius:10,border:`1px dashed ${T.border}`,background:T.card,cursor:"pointer",color:T.muted,fontSize:13,display:"inline-flex",alignItems:"center",gap:8}}>
                <ServicePhotoAddPhoneIcon size={16}/> {row.photo?"Trocar":"Adicionar"}
              </button>
              {row.originalName&&<span style={{fontSize:10,color:T.muted,maxWidth:180,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{row.originalName}</span>}
            </div>
          </Field>
          <button type="button" onClick={()=>setSvcPhotoForm(prev=> ({...prev, rows:(prev.rows||[]).length===1 ? prev.rows : (prev.rows||[]).filter(x=>x.id!==row.id)}))} style={{background:"none",border:"none",color:(svcPhotoForm.rows||[]).length===1?T.border:T.red,cursor:(svcPhotoForm.rows||[]).length===1?"default":"pointer",fontSize:22,paddingBottom:8}}>×</button>
        </div>)}
        <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
          <Btn variant="ghost" onClick={()=>{ const existingCount=getServicePhotoEntries(services.find(s=>s.id===svcPhotoForm.svcId)||{}).length; const remaining=Math.max(0,10-existingCount); if((svcPhotoForm.rows||[]).length<remaining){ const initialTitle=servicePhotoTitleOptions[0]||""; setSvcPhotoForm(prev=>({...prev,rows:[...(prev.rows||[]),{id:Date.now()+Math.random(),title:initialTitle,photo:"",thumb:"",originalName:""}]})); } }} disabled={(svcPhotoForm.rows||[]).length>=Math.max(0,10-getServicePhotoEntries(services.find(s=>s.id===svcPhotoForm.svcId)||{}).length)}>{(svcPhotoForm.rows||[]).length<Math.max(0,10-getServicePhotoEntries(services.find(s=>s.id===svcPhotoForm.svcId)||{}).length)?"+ Nova Foto":"Limite atingido"}</Btn>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setSvcPhotoForm(null)}>Cancelar</Btn>
            <Btn onClick={saveInlineServicePhotos}>Salvar Fotos</Btn>
          </div>
        </div>
      </div></div></div>}

      {servicePhotoLightbox&&<div onClick={()=>setServicePhotoLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.92)",zIndex:1400,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,padding:16,cursor:"zoom-out"}}><img src={servicePhotoLightbox.photos[servicePhotoLightbox.idx]?.photo||servicePhotoLightbox.photos[servicePhotoLightbox.idx]?.url||servicePhotoLightbox.photos[servicePhotoLightbox.idx]?.thumb} alt="" style={{maxWidth:"92vw",maxHeight:"78vh",objectFit:"contain",borderRadius:12,boxShadow:"0 18px 40px rgba(0,0,0,.45)",cursor:"auto"}} onClick={e=>e.stopPropagation()}/><div onClick={e=>e.stopPropagation()} style={{display:"flex",alignItems:"center",gap:14,background:"rgba(15,23,42,.78)",border:"1px solid rgba(255,255,255,.12)",borderRadius:999,padding:"8px 14px"}}>{servicePhotoLightbox.photos.length>1&&<><button onClick={()=>setServicePhotoLightbox(lb=>({...lb,idx:(lb.idx-1+lb.photos.length)%lb.photos.length}))} style={{background:"none",border:"none",color:"#fff",fontSize:28,cursor:"pointer",lineHeight:1}}>‹</button><span style={{color:"#E5E7EB",fontSize:12,minWidth:60,textAlign:"center"}}>{servicePhotoLightbox.idx+1}/{servicePhotoLightbox.photos.length}</span><button onClick={()=>setServicePhotoLightbox(lb=>({...lb,idx:(lb.idx+1)%lb.photos.length}))} style={{background:"none",border:"none",color:"#fff",fontSize:28,cursor:"pointer",lineHeight:1}}>›</button></>}<button onClick={()=>setServicePhotoLightbox(null)} style={{background:"none",border:"none",color:"#fff",fontSize:22,cursor:"pointer",lineHeight:1}}>×</button></div></div>}

      {/* ── HEADER ───────────────────────────────────────────────── */}
      {!selected&&<>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <h2 style={{margin:0,color:T.text,fontSize:20,fontWeight:800}}>{mode==="external" ? "👷 Serviços Terceirizados" : "🔨 Serviços Internos"}</h2>
            <div style={{fontSize:12,color:T.muted,marginTop:2}}>
              {mode==="external" ? `${currentExternalOpen} serviços terceirizados em aberto` : `${currentInternalOpen} serviços em aberto`}
            </div>
          </div>
          {tab!=="providers" && canCreate&&<Btn onClick={()=>setForm(defForm(tab))}>+ {mode==="external"?"Novo Serviço Terceirizado":"Novo Serviço"}</Btn>}
        </div>

        {(()=>{
          const cards = filterConfiguredCards([
            { id:"abertas", node:<StatCard icon={mode==="external"?"👷":"🔨"} label="Abertos" value={openServicesCount} color={mode==="external"?"#0D9488":T.accent} onClick={()=>{ setCardFilter("open"); setFStatus("Todos"); }}/> },
            { id:"em_execucao", node:<StatCard icon="⚡" label="Em Execução" value={runningServicesCount} color={T.yellow} onClick={()=>{ setCardFilter("running"); setFStatus("Todos"); }}/> },
            { id:"aguardando_decisao", node:<StatCard icon="⏳" label="Aguardando Decisão" value={waitingServicesCount} color={T.accent} onClick={()=>{ setCardFilter("waiting"); setFStatus("Todos"); }}/> },
            { id:"concluidas", node:<StatCard icon="✅" label="Concluídos" value={concludedServicesCount} color={T.green} onClick={()=>{ setCardFilter("done"); setFStatus("Todos"); }}/> },
          ], allowedServiceCards, ["abertas","em_execucao","aguardando_decisao","concluidas"]);
          return cards.length ? <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:12,marginBottom:16}}>{cards.map(item=><React.Fragment key={item.id}>{item.node}</React.Fragment>)}</div> : null;
        })()}

        {/* ── NEW FORM INLINE UNDER CARDS ───────────────────────────── */}
      {form&&form._new&&<div style={{marginBottom:16}}><button onClick={()=>setForm(null)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button><div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}><div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14}}>{form?(form._new?(form.type==="occurrence"?"Nova Ocorrência":form.type==="external"?"Novo Serviço de Terceiro":"Novo Serviço Interno"):(form.type==="occurrence"?"Editar Ocorrência":"Editar Serviço")):""}</div><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>

          {/* Title from list */}
          <Field label="Título *" col="1/-1">
            <Select value={form.title||""} onChange={e=>setForm(f=>({...f,title:e.target.value}))}>
              <option value="">Selecione o serviço...</option>
              {(SL.SERVICE_TITLES||[]).map(t=><option key={t}>{t}</option>)}
            </Select>
          </Field>

          {/* Priority — dynamic per type */}
          <Field label="Prioridade *" col="1/-1">
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {getPriorities(form.type).map(p=>(
                <div key={p.id} onClick={()=>setForm(f=>({...f,priority:p.id,...(f.type==="occurrence"?{urgency:p.label}:{} )}))}
                  style={{flex:form.type==="occurrence"&&isCompactViewport()?"1 1 calc(50% - 6px)":"1 1 auto",padding:"6px 10px",borderRadius:8,cursor:"pointer",textAlign:"center",
                    border:`2px solid ${form.priority===p.id?p.color:T.border}`,
                    background:form.priority===p.id?`${p.color}18`:T.card2,transition:"all .15s"}}>
                  <div style={{fontSize:12,fontWeight:700,color:form.priority===p.id?p.color:T.muted}}>{p.icon} {p.label}</div>
                </div>
              ))}
            </div>
          </Field>

          <Field label="Data" col={isCompactViewport()?"1/-1":undefined}><Input type="date" value={form.date||today()} onChange={e=>setForm(f=>({...f,date:e.target.value}))}/></Field>
          {!form._new&&form.type!=="occurrence"&&(
            <Field label="Status" col={isCompactViewport()?"1/-1":undefined}>
              <Select value={form.status||"Aberto"} onChange={e=>setForm(f=>({...f,status:e.target.value,nfPending:e.target.value!=="Concluído"?false:(f.nfPending||false)}))}>
                {getEditableServiceStatuses(form.type, form).map(s=><option key={s}>{s}</option>)}
              </Select>
            </Field>
          )}

          {/* Assignees — for internal and occurrence */}
          {(form.type==="internal"||form.type==="occurrence")&&(
            <Field label="Encaminhar para Colaborador(es)">
              <Select value="" onChange={e=>{ const emp=(employees||[]).find(x=>String(x.id)===String(e.target.value)); if(emp){ setForm(f=>{ const current=Array.isArray(f.assignees)?f.assignees:[]; return current.some(x=>String((x&&typeof x==="object")?x.id:x)===String(emp.id)) ? f : ({...f, assignees:[...current,{ id:emp.id, name:emp.name, username:emp.username||"", employeeId:emp.id, linkedAccountId:emp.linkedAccountId||"", userId:emp.userId||"" }]}); }); } e.target.value=""; }}>
                <option value="">+ Adicionar colaborador...</option>
                {employees.filter(e=>e.status==="Ativo"||!e.status).map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
              </Select>
              {(form.assignees||[]).length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                {(form.assignees||[]).map(a=>({ raw:a, id:String((a&&typeof a==="object")?a.id:a), label:(a&&typeof a==="object")?(a.name||a.displayName||a.id):String(a||"") })).map(entry=>(
                  <span key={entry.id||entry.label} style={{fontSize:11,background:T.accent+"22",color:T.accent,borderRadius:20,padding:"2px 8px",display:"flex",alignItems:"center",gap:4}}>
                    {entry.label}<button onClick={()=>setForm(f=>({...f,assignees:(f.assignees||[]).filter(x=>String((x&&typeof x==="object")?x.id:x)!==String(entry.id||entry.label))}))} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:12,padding:0}}>×</button>
                  </span>
                ))}
              </div>}
            </Field>
          )}

          {/* Providers — for external */}
          {form.type==="external"&&(<>
            <Field label="Prestador(es)">
              <Select value="" onChange={e=>{if(e.target.value&&!(form.providersList||[]).includes(e.target.value))setForm(f=>({...f,providersList:[...(f.providersList||[]),e.target.value]}));}}>
                <option value="">+ Adicionar prestador...</option>
                {providerDirectory.map(p=><option key={p.id} value={p.name}>{p.name} — {p.specialty || p.serviceCategories?.[0] || "Prestador"}</option>)}
              </Select>
              {(form.providersList||[]).length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                {(form.providersList||[]).map(p=>(
                  <span key={p} style={{fontSize:11,background:"#0D948822",color:"#0D9488",borderRadius:20,padding:"2px 8px",display:"flex",alignItems:"center",gap:4}}>
                    {p}<button onClick={()=>setForm(f=>({...f,providersList:(f.providersList||[]).filter(x=>x!==p)}))} style={{background:"none",border:"none",color:"#0D9488",cursor:"pointer",fontSize:12,padding:0}}>×</button>
                  </span>
                ))}
              </div>}
            </Field>
            <Field label="Responsável pelo Contato">
              <Select value={form.contactPersonId||""} onChange={e=>{ const emp=(employees||[]).find(x=>String(x.id)===String(e.target.value)); setForm(f=>({...f,contactPerson:emp?.name||"",contactPersonId:emp?.id||"",contactPersonUsername:emp?.username||""})); }}>
                <option value="">Selecione...</option>
                {employees.filter(e=>e.status==="Ativo"||!e.status).map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
              </Select>
            </Field>
            {/* Misto: also add collaborators */}
            <Field label="Colaboradores (se misto)" col="1/-1">
              <Select value="" onChange={e=>{ const emp=(employees||[]).find(x=>String(x.id)===String(e.target.value)); if(emp){ setForm(f=>{ const current=Array.isArray(f.assignees)?f.assignees:[]; return current.some(x=>String((x&&typeof x==="object")?x.id:x)===String(emp.id)) ? f : ({...f, assignees:[...current,{ id:emp.id, name:emp.name, username:emp.username||"", employeeId:emp.id, linkedAccountId:emp.linkedAccountId||"", userId:emp.userId||"" }]}); }); } e.target.value=""; }}>
                <option value="">+ Colaborador (opcional)...</option>
                {employees.filter(e=>e.status==="Ativo"||!e.status).map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
              </Select>
              {(form.assignees||[]).length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                {(form.assignees||[]).map(a=>({ raw:a, id:String((a&&typeof a==="object")?a.id:a), label:(a&&typeof a==="object")?(a.name||a.displayName||a.id):String(a||"") })).map(entry=>(
                  <span key={entry.id||entry.label} style={{fontSize:11,background:T.accent+"22",color:T.accent,borderRadius:20,padding:"2px 8px",display:"flex",alignItems:"center",gap:4}}>
                    {entry.label}<button onClick={()=>setForm(f=>({...f,assignees:(f.assignees||[]).filter(x=>String((x&&typeof x==="object")?x.id:x)!==String(entry.id||entry.label))}))} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:12,padding:0}}>×</button>
                  </span>
                ))}
              </div>}
            </Field>
          </>)}

          <Field label="Centro de custo">
            <Select value={form.costCenter||bridgeCostCenter||allowedCostCenters[0]||"Sede"} onChange={e=>setForm(f=>({...f,costCenter:e.target.value}))} disabled={bridgeScoped}>
              {(bridgeScoped ? [bridgeCostCenter] : (allowedCostCenters.length ? allowedCostCenters : (SL.COST_CENTERS||["Sede","Haras","Pecuária"]))).map(c=><option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>

          {form.type!=="occurrence"&&(
            <Field label="Categoria">
              <Select value={form.category||"other"} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {OCCURRENCE_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </Select>
            </Field>
          )}

          {/* Location */}
          <Field label="Localização / Pasto">
            <Select value={form.location||""} onChange={e=>setForm(f=>({...f,location:e.target.value,poiId:""}))}>
              <option value="">Selecione o local...</option>
              {pastures.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Ponto de Interesse">
            <Select value={form.poiId||""} onChange={e=>setForm(f=>({...f,poiId:e.target.value}))}>
              <option value="">Sem POI</option>
              {pois.map(p=><option key={p.id} value={p.id}>{p.name||p.label}</option>)}
            </Select>
          </Field>
          {(form.location || form.poiId) && <div style={{gridColumn:"1/-1",background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:"12px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}><div style={{fontWeight:700,color:T.text,fontSize:13}}>🌦️ Leitura climática para este registro</div>{formClimatePanel?.meta&&<Badge color={formClimatePanel.meta.color}>{formClimatePanel.meta.icon} {formClimatePanel.meta.label}</Badge>}</div>
            {formClimateLoading&&<div style={{fontSize:12,color:T.muted}}>Carregando leitura climática...</div>}
            {!formClimateLoading&&formClimatePanel?.error&&<div style={{fontSize:12,color:T.muted}}>{formClimatePanel.error}</div>}
            {!formClimateLoading&&!formClimatePanel?.error&&formClimatePanel&&<>
              <div style={{fontSize:12,color:T.text,fontWeight:700,marginBottom:4}}>{formClimatePanel.headline}</div>
              <div style={{fontSize:11,color:T.muted,marginBottom:8}}>{formClimatePanel.recommendation}</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{formClimatePanel.signals.map(signal=><React.Fragment key={signal.id}>{renderClimateToneBadge(signal.tone, `${signal.icon} ${signal.label}`, signal.value)}</React.Fragment>)}</div>
            </>}
          </div>}
          <Field label="Maquinário Envolvido">
            <Select value={form.machineId||""} onChange={e=>setForm(f=>({...f,machineId:e.target.value}))}>
              <option value="">Sem maquinário</option>
              {machinery.map(m=><option key={m.id} value={m.id}>{m.name}{m.plate?` (${m.plate})`:""}</option>)}
            </Select>
          </Field>
          <Field label="Tipo de Execução">
            <Select value={form.execType||""} onChange={e=>setForm(f=>({...f,execType:e.target.value}))}>
              <option value="">Selecione...</option>
              {(execTypeOptions||["Interno","Externo","Misto"]).map(t=><option key={t}>{t}</option>)}
            </Select>
          </Field>

          {form.type==="occurrence"&&(<>
            <Field label="Categoria *">
              <Select value={form.category||"other"} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {OCCURRENCE_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </Select>
            </Field>
            <Field label="Prioridade" col="1/-1">
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {getPriorities("occurrence").map(p=>(
                  <div key={p.id} onClick={()=>setForm(f=>({...f,priority:p.id,urgency:p.label}))}
                    style={{flex:isCompactViewport()?"1 1 calc(50% - 6px)":"1 1 auto",padding:"6px 10px",borderRadius:8,cursor:"pointer",textAlign:"center",
                      border:`2px solid ${(form.priority||"normal")===p.id?p.color:T.border}`,
                      background:(form.priority||"normal")===p.id?`${p.color}18`:T.card2,transition:"all .15s"}}>
                    <div style={{fontSize:12,fontWeight:700,color:(form.priority||"normal")===p.id?p.color:T.muted}}>{p.icon} {p.label}</div>
                  </div>
                ))}
              </div>
            </Field>
          </>)}

          <Field label="Descrição / Observação" col="1/-1">
            <Textarea rows={3} value={form.description||""} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Descreva o serviço, situação encontrada, observações..."/>
          </Field>

          <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between"}}>
            <div>{!form._new&&canDelete&&<Btn variant="danger" onClick={()=>requestServiceDelete(form)}>🗑️ Excluir</Btn>}</div>
            <div style={{display:"flex",gap:10}}>
              <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
              {((form&&form._new&&canCreate)||(form&&!form._new&&canEditService))&&<Btn onClick={saveService}>Salvar</Btn>}
            </div>
          </div>
        </div></div></div>}


        {!(form&&form._new)&&<>
        {/* Tabs + Filters */}
        <ModuleHeaderMenu
          T={T}
          title="Menu de Serviços"
          items={visibleTabs.map(t=>({
            id:t.id,
            label:t.label,
            icon:t.icon,
            color:t.color,
            badge:t.count,
            active:tab===t.id,
            onClick:()=>setTab(t.id),
          }))}
        />
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:12}}>
          {tab!=="providers" && (
            <div>
              <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Filtros</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:8,alignItems:"center"}}>
                <Select value={filterStatus} onChange={e=>{ setCardFilter("all"); setFStatus(e.target.value); }} style={{width:"100%",minWidth:0}}>
                  <option value="Todos">Status</option>
                  {Object.keys(SVC_STATUS_FLOW).map(s=><option key={s}>{s}</option>)}
                </Select>
                <Select value={filterType} onChange={e=>setFType(e.target.value)} style={{width:"100%",minWidth:0}}>
                  <option value="Todos">Tipos</option>
                  {serviceTypeOptions.map(s=><option key={s}>{s}</option>)}
                </Select>
              </div>
            </div>
          )}
        </div>

        {tab==="providers" ? (
          <div style={{marginTop:4}}>
            <ProvidersModule session={session} isGestor={isGestor}/>
          </div>
        ) : (
        <>
        {/* List */}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {tabItems.map(item=>{
            const days    = daysSince(item.createdAt);
            const execClr = item.execDate?dateColor(item.execDate):null;
            const delClr  = item.deliveryDate?dateColor(item.deliveryDate):null;
            const serviceCat = OCCURRENCE_CATEGORIES.find(c=>c.id===item.category);
            const closed  = ["Concluído","Cancelado","Negado","Concluída","Cancelada"].includes(item.status);
            return(
              <div key={item.id} onClick={()=>setSelected(item)}
                style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"12px 16px",
                  cursor:"pointer",transition:"all .15s",
                  borderLeft:`3px solid ${SVC_TYPES[item.type]?.color||T.accent}`,opacity:closed?.7:1}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
                onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",gap:5,marginBottom:3,flexWrap:"wrap"}}>
                      <StBadge status={item.status||"Aberto"}/>
                      {serviceCat&&<Badge color={T.muted}>{serviceCat.icon} {serviceCat.label}</Badge>}
                      <PrBadge type={item.type} priority={item.priority}/>
                      {item.type==="external"&&item.status==="Concluído"&&item.nfPending&&!item.nfNum&&<Badge color={T.yellow}>📄 Falta Nota Fiscal</Badge>}
                    </div>
                    <div style={{fontSize:11,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayServiceRecordNumber(item)}</div>
                    <div style={{fontWeight:700,color:T.text,fontSize:14}}>{item.title}{item.costCenter?` - ${item.costCenter}`:""}</div>
                    <div style={{fontSize:11,color:T.muted,marginTop:3,display:"flex",gap:10,flexWrap:"wrap"}}>
                      <span>📅 {fmtDate(item.date||item.createdAt||"")}</span>
                      <span style={{color:days>30?T.red:days>15?T.yellow:T.muted}}>⏱ {days}d</span>
                      {item.location&&<span>📍 {item.location}</span>}
                      {(item.assignees||[]).length>0&&<span>👤 {(item.assignees||[]).map(a=>a?.name||a).join(", ")}</span>}
                      {item.execDate&&<span style={{color:execClr||T.muted}}>⚙️ {item.execDate}</span>}
                      {item.deliveryDate&&<span style={{color:delClr||T.muted}}>🏁 {item.deliveryDate}</span>}
                    </div>
                    {item.status==="Pausada" && (item.pauseReason||item.pauseNote) && (
                      <div style={{fontSize:11,color:T.orange||T.yellow,marginTop:6,background:`${(T.orange||T.yellow)}15`,border:`1px solid ${(T.orange||T.yellow)}33`,borderRadius:8,padding:"6px 8px"}}>
                        ⏸️ <strong>Justificativa:</strong> {item.pauseReason||"—"}{item.pauseNote?` — ${item.pauseNote}`:""}
                      </div>
                    )}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                    {item.type==="external"&&item.status==="Concluído"&&item.nfPending&&!item.nfNum&&(
                      <Btn size="sm" variant="ghost" onClick={e=>{e.stopPropagation();setNfForm({svcId:item.id,nfNum:item.nfNum||"",nfPhoto:item.nfPhoto||""});}}>📄 Inserir NF</Btn>
                    )}
                    {item.type!=="occurrence" && item._source!=="occurrence" && <div title={(getServicePhotoEntries(item).length>0?`${getServicePhotoEntries(item).length} foto(s) no serviço`:`Sem fotos no serviço`)} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:999,border:`1px solid ${getServicePhotoEntries(item).length>0?`${T.accent}44`:T.border}`,background:getServicePhotoEntries(item).length>0?`${T.accent}14`:T.card2,color:getServicePhotoEntries(item).length>0?T.accent:T.muted,fontSize:11,fontWeight:700}}><ServicePhotoStatusIcon size={16} hasPhotos={getServicePhotoEntries(item).length>0}/><span>{getServicePhotoEntries(item).length>0?getServicePhotoEntries(item).length:"0"}</span></div>}
                    <div style={{fontSize:12,color:T.accent,whiteSpace:"nowrap"}}>Ver →</div>
                  </div>
                </div>
              </div>
            );
          })}
          {tabItems.length===0&&<div style={{color:T.muted,textAlign:"center",padding:48}}>Nenhum registro encontrado.</div>}
        </div>
        </>
        )}
        </>}
      </>}

      {/* ── DETAIL VIEW ──────────────────────────────────────────── */}
      {selected&&(()=>{
        const isOcc = selected._source==="occurrence";
        const item  = (isOcc
          ? occurrences.find(o=>o.id===selected.id)
          : services.find(s=>s.id===selected.id)) || selected;
        const days   = daysSince(item.createdAt);
        const execClr= item.execDate?(dateColor(item.execDate)||T.green):T.muted;
        const delClr = item.deliveryDate?(dateColor(item.deliveryDate)||T.green):T.muted;
        const closed = ["Concluído","Cancelado","Negado","Concluída","Cancelada"].includes(item.status);

        return(
          <div>
            <button onClick={()=>{ if(onClearOpenRecord) onClearOpenRecord(); if(onBridgeBack) onBridgeBack(); else setSelected(null); }} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button>

            {/* Header */}
            <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px",marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                    <TyBadge type={item.type||"internal"}/>
                    <StBadge status={item.status||"Aberto"}/>
                    <PrBadge type={item.type} priority={item.priority}/>
                  </div>
                  <div style={{fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7,marginBottom:4}}>{displayServiceRecordNumber(item)}</div>
                  <div style={{fontWeight:800,color:T.text,fontSize:18}}>{item.title}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:4}}>📅 {fmtDate(item.date||item.createdAt||"")} · ⏱ {days}d</div>
                </div>
                {canEditItem(item)&&<Btn size="sm" variant="ghost" onClick={()=>setForm({...item,_new:false,type:isOcc?"occurrence":(item.type||"internal")})}>✏️ Editar</Btn>}
              </div>

              {item.description&&<div style={{fontSize:13,color:T.muted,background:T.card2,borderRadius:8,padding:"8px 12px",marginBottom:10}}>{item.description}</div>}
              {item.type==="external"&&item.status==="Concluído"&&item.nfPending&&!item.nfNum&&(
                <div style={{background:`${T.yellow}15`,border:`1px solid ${T.yellow}44`,borderRadius:8,padding:"8px 12px",marginBottom:10,fontSize:12,color:T.yellow}}>
                  ⚠️ Concluído, mas falta lançar a nota fiscal.
                </div>
              )}

              {/* Info grid */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8,marginBottom:10}}>
                {item.location&&<div style={{fontSize:12,color:T.muted}}>📍 <strong style={{color:T.text}}>{item.location}</strong></div>}
                {item.machineId&&(()=>{const m=machinery.find(x=>String(x.id)===String(item.machineId));return m?<div style={{fontSize:12,color:T.muted}}>🚜 <strong style={{color:T.text}}>{m.name}</strong></div>:null;})()}
                {item.category&&(()=>{const cat=OCCURRENCE_CATEGORIES.find(c=>c.id===item.category);return cat?<div style={{fontSize:12,color:T.muted}}>{cat.icon} <strong style={{color:T.text}}>{cat.label}</strong></div>:null;})()}
                {(item.assignees||[]).length>0&&<div style={{fontSize:12,color:T.muted}}>👤 <strong style={{color:T.text}}>{(item.assignees||[]).map(a=>a?.name||a).join(", ")}</strong></div>}
                {(item.providersList||[]).length>0&&<div style={{fontSize:12,color:T.muted}}>👷 <strong style={{color:T.text}}>{item.providersList.join(", ")}</strong></div>}
                {item.contactPerson&&<div style={{fontSize:12,color:T.muted}}>📞 Contato: <strong style={{color:T.text}}>{item.contactPerson}</strong></div>}
                {item.execDate&&<div style={{fontSize:12}}>⚙️ <strong style={{color:execClr}}>{item.execDate}</strong></div>}
                {item.deliveryDate&&<div style={{fontSize:12}}>🏁 <strong style={{color:delClr}}>{item.deliveryDate}</strong></div>}
                {item.nfNum&&<div style={{fontSize:12,color:T.muted}}>📄 NF: <strong style={{color:T.text}}>{item.nfNum}</strong></div>}
                {item.type==="external"&&item.status==="Concluído"&&item.nfPending&&!item.nfNum&&<div style={{fontSize:12,color:T.yellow}}>📄 <strong>Falta Nota Fiscal</strong></div>}
              </div>

              <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px",marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}><div style={{fontWeight:700,color:T.text,fontSize:13}}>🌦️ Condição climática operacional</div>{selectedClimatePanel?.meta&&<Badge color={selectedClimatePanel.meta.color}>{selectedClimatePanel.meta.icon} {selectedClimatePanel.meta.label}</Badge>}</div>
                {selectedClimateLoading&&<div style={{fontSize:12,color:T.muted}}>Carregando leitura climática...</div>}
                {!selectedClimateLoading&&selectedClimatePanel?.error&&<div style={{fontSize:12,color:T.muted}}>{selectedClimatePanel.error}</div>}
                {!selectedClimateLoading&&!selectedClimatePanel?.error&&selectedClimatePanel&&<>
                  <div style={{fontSize:13,color:T.text,fontWeight:700,marginBottom:4}}>{selectedClimatePanel.headline}</div>
                  <div style={{fontSize:12,color:T.muted,marginBottom:8}}>{selectedClimatePanel.recommendation}</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>{selectedClimatePanel.signals.map(signal=><React.Fragment key={signal.id}>{renderClimateToneBadge(signal.tone, `${signal.icon} ${signal.label}`, signal.value)}</React.Fragment>)}</div>
                  <div style={{fontSize:11,color:T.muted,lineHeight:1.45}}>📍 {selectedClimatePanel.contextLabel} · 🛰️ {selectedClimatePanel.sourceLabel}{selectedClimatePanel.observedLabel?` · leitura ${fmtDate(selectedClimatePanel.observedLabel)}`:""}</div>
                </>}
              </div>

              {/* Budget */}
              {item.budget&&<div style={{background:`${T.purple}12`,border:`1px solid ${T.purple}33`,borderRadius:8,padding:"10px 14px",marginBottom:10}}>
                <div style={{fontWeight:700,color:T.purple,fontSize:13,marginBottom:4}}>💰 Orçamento</div>
                <div style={{fontSize:12}}>
                  <strong>{item.budget.provider}</strong>
                  {" · "}Serviço: <strong>R$ {fmtMoney(item.budget.value)}</strong>
                  {item.budget.partsCost>0&&<span> · Peças: <strong>R$ {fmtMoney(item.budget.partsCost)}</strong></span>}
                  {" · "}Total: <strong style={{color:T.green}}>R$ {fmtMoney((item.budget.value||0)+(item.budget.partsCost||0))}</strong>
                  {item.budget.payMethod&&<span style={{color:T.muted}}> · {item.budget.payMethod}</span>}
                </div>
                {item.budget.obs&&<div style={{fontSize:11,color:T.muted,marginTop:4}}>{item.budget.obs}</div>}
                {item.approvedBy&&<div style={{fontSize:11,color:T.green,marginTop:4}}>✅ Aprovado por {item.approvedBy}</div>}
              </div>}

              {item.denyReason&&<div style={{background:`${T.red}12`,border:`1px solid ${T.red}33`,borderRadius:8,padding:"8px 12px",marginBottom:10,fontSize:12,color:T.red}}>❌ Negado — {item.denyReason}</div>}
              {item.status==="Pausada" && (item.pauseReason||item.pauseNote)&&<div style={{background:`${(T.orange||T.yellow)}15`,border:`1px solid ${(T.orange||T.yellow)}33`,borderRadius:8,padding:"8px 12px",marginBottom:10,fontSize:12,color:(T.orange||T.yellow)}}>⏸️ <strong>Justificativa da pausa:</strong> {item.pauseReason||"—"}{item.pauseNote?` — ${item.pauseNote}`:""}</div>}


              {/* Materials used */}
              {(item.materials||[]).length>0&&<div style={{marginBottom:10}}>
                <div style={{fontSize:12,fontWeight:700,color:T.muted,marginBottom:6}}>📦 Materiais utilizados</div>
                {(item.materials||[]).some(m=>m?.stockId && (m.pendingUsage || m.qtyUsed===null || m.qtyUsed==="" || typeof m.qtyUsed==="undefined"))&&<div style={{background:`${T.yellow}15`,border:`1px solid ${T.yellow}44`,borderRadius:8,padding:"8px 12px",marginBottom:8,fontSize:12,color:T.yellow,lineHeight:1.45}}>⚠️ Há materiais com quantidade utilizada pendente. Se ela não for informada, valerá a quantidade separada já baixada do estoque.</div>}
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {(item.materials||[]).filter(m=>m.stockId&&(m.qtyUsed||m.qty||m.qtySeparated)).map((m,i)=>{
                    const st=enrichedStockItems.find(s=>String(s.id)===String(m.stockId));
                    const pending = !!(m.pendingUsage || m.qtyUsed===null || m.qtyUsed==="" || typeof m.qtyUsed==="undefined");
                    const draftKey = `${item.id}::${i}`;
                    return <div key={i} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 12px"}}>
                      <div style={{fontSize:12,color:T.text,fontWeight:700,marginBottom:4}}>{m.name||st?.name||m.stockId}</div>
                      <div style={{fontSize:12,color:T.muted,marginBottom:pending?8:0}}>Separado {m.qtySeparated||m.qty||0} / utilizado {pending?"pendente":(m.qtyUsed??m.qty??m.qtySeparated)} {m.unit||st?.unit||""}</div>
                      {pending&&<div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"160px auto",gap:8,alignItems:"end"}}>
                        <Field label="Quantidade utilizada"><Input type="number" min="0" step="0.001" value={serviceMaterialUsageDrafts[draftKey]??""} onChange={e=>setServiceMaterialUsageDrafts(prev=>({...prev,[draftKey]:e.target.value}))} placeholder={m.unit||st?.unit||""}/></Field>
                        <Btn size="sm" onClick={()=>saveServiceMaterialUsage(item.id, i, serviceMaterialUsageDrafts[draftKey])}>Salvar utilizada</Btn>
                      </div>}
                    </div>;
                  })}
                </div>
              </div>}
              {!isOcc&&!canOperateService(item)&&session?.role==="solicitante"&&!closed&&(
                <div style={{marginTop:12,marginBottom:8,fontSize:11,color:T.muted,background:`${T.yellow}12`,border:`1px solid ${T.yellow}33`,borderRadius:8,padding:"8px 10px"}}>
                  🔒 Somente colaboradores designados neste serviço podem agendar, iniciar, pausar ou concluir.
                </div>
              )}

              {/* Action buttons — admin/gestor livres; colaborador apenas se designado */}
              {!isOcc&&(canOperateService(item) || canEditService || isGestor || isAdmin)&&(!closed||isAdmin||isGestor)&&<div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:12}}>
                {!closed&&<>
                  {(item.status==="Aberto"||item.status==="Agendado")&&
                    <Btn size="sm" disabled={!canOperateService(item)} onClick={()=>setSchedForm({svcId:item.id,execDate:item.execDate||"",deliveryDate:item.deliveryDate||""})}>📅 Agendar</Btn>}
                  {item.type==="external"&&!item.budget&&item.status!=="Aprovado"&&
                    <Btn size="sm" variant="ghost" onClick={()=>setBudgetForm({svcId:item.id,provider:"",value:"",partsCost:"",obs:""})}>💰 Orçamento</Btn>}
                  {item.status==="Aguardando Aprovação"&&canApprove&&<>
                    <Btn size="sm" onClick={()=>approveBudget(item.id)}>✅ Aprovar</Btn>
                    <Btn size="sm" variant="danger" onClick={()=>setDenyForm({svcId:item.id})}>❌ Negar</Btn>
                  </>}
                  {!(["Em Execução","Concluído","Cancelado","Negado"].includes(item.status))&&
                    <Btn size="sm" disabled={!canOperateService(item)} onClick={()=>updateSvc(item.id,{status:"Em Execução",startedAt:item.startedAt||new Date().toISOString(),resumedAt:item.status==="Pausada"?new Date().toISOString():item.resumedAt||""},{action:item.status==="Pausada"?"Tarefa reiniciada":"Em Execução"})}>{item.status==="Pausada"?"▶️ Recomeçar":"⚙️ Iniciar"}</Btn>}
                  {item.status==="Em Execução"&&
                    <Btn size="sm" variant="ghost" disabled={!canOperateService(item)} onClick={()=>setPauseForm({svcId:item.id,reason:(pauseReasonOptions[0]||""),obs:""})}>⏸️ Pausar Tarefa</Btn>}
                  {item.status==="Em Execução"&&
                    <Btn size="sm" variant="ghost" onClick={()=>setMatForm({svcId:item.id,items:[{stockId:"",qtySeparated:"",qtyUsed:""}]})}>📦 Materiais</Btn>}
                  {(canAddPhotosToService(item) || getServicePhotoEntries(item).length>0)&&
                    <Btn size="sm" variant="ghost" onClick={()=>canAddPhotosToService(item)?openInlineServicePhotos(item.id):null} disabled={!canAddPhotosToService(item)}><span style={{display:"inline-flex",alignItems:"center",gap:6}}><ServicePhotoAddPhoneIcon size={16}/> Fotos</span></Btn>}
                  {item.status==="Em Execução"&&item.type!=="external"&&
                    <Btn size="sm" disabled={!canOperateService(item)} onClick={()=>updateSvc(item.id,{status:"Concluído",completedAt:item.completedAt||new Date().toISOString()},{action:"Concluído"})}>✅ Concluir</Btn>}
                  {item.status==="Em Execução"&&item.type==="external"&&<>
                    <Btn size="sm" disabled={!canOperateService(item)} onClick={()=>openInlineNfForm(item.id)}>📄 Concluir c/ NF</Btn>
                    <Btn size="sm" variant="ghost" disabled={!canOperateService(item)} onClick={()=>{ updateSvc(item.id,{status:"Concluído",completedAt:item.completedAt||new Date().toISOString(),nfPending:true,nfNum:item.nfNum||"",nfPhoto:item.nfPhoto||""},{action:"Serviço concluído sem nota fiscal",note:"Concluído, mas falta nota fiscal"}); appendFinancialEntry({ originId:`${item.id}:pending_nf:${Date.now()}`, description:`Serviço terceirizado concluído · ${item.title||item.id}`, amount:Number(item?.budget?.value||0), costCenter:item.costCenter||item.category||'Sede', paymentMethod:item?.budget?.payMethod||'', status:'Pendente', notes:`Prestador: ${item?.budget?.provider||item?.providerName||''}`, justificationMissing:'Concluído sem NF; complementar número, valor final, agendamento e forma de pagamento no financeiro.', metadata:{ stage:'service_completed_pending_nf', serviceId:item.id } }); }}>✅ Concluir s/ NF</Btn>
                  </>}
                  {item.type==="external"&&item.status==="Concluído"&&(!item.nfNum||item.nfPending)&&
                    <Btn size="sm" variant="ghost" onClick={()=>openInlineNfForm(item.id)}>📄 Inserir NF</Btn>}
                  {closed&&canEditService&&
                    <Btn size="sm" variant="ghost" onClick={()=>setForm({...item,_new:false})}>✏️ Reabrir / Editar</Btn>}
                  {canApprove&&<Btn size="sm" variant="danger" onClick={()=>updateSvc(item.id,{status:"Cancelado"},{action:"Cancelado"})}>🚫 Cancelar</Btn>}
                </>}
                
              </div>}
            </div>

            {/* Timeline */}
            {(((item.timeline||[]).length>0) || (!isOcc && getServicePhotoBatches(item).length>0))&&(
              <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px"}}>
                <div style={{fontWeight:700,color:T.text,fontSize:14,marginBottom:12}}>📋 Histórico</div>
                {!isOcc && getServicePhotoBatches(item).length>0 && <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:(item.timeline||[]).length>0?14:0}}>
                  {getServicePhotoBatches(item).map((batch,bIdx)=>(
                    <div key={batch.key||bIdx} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:10,flexWrap:"wrap"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,fontWeight:700,color:T.text,fontSize:13}}>
                          <ServicePhotoStatusIcon size={18} hasPhotos={true}/> Fotos adicionadas
                        </div>
                        <div style={{fontSize:10,color:T.muted}}>{batch.addedBy||"Usuário"}{batch.addedAt?` · ${new Date(batch.addedAt).toLocaleString("pt-BR")}`:""}</div>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(88px,88px))",gap:10,justifyContent:"flex-start"}}>
                        {batch.photos.map((ph,idx)=>(
                          <div key={ph.id||`${batch.key}-${idx}`} style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-start"}}>
                            <img src={ph.thumb||ph.photo||ph.url} alt="" style={{width:88,height:88,objectFit:"cover",borderRadius:10,cursor:"zoom-in",border:`1px solid ${T.border}`,boxShadow:"0 4px 12px rgba(0,0,0,.16)"}} onClick={()=>setServicePhotoLightbox({photos:batch.photos,idx})}/>
                            <div style={{fontSize:10,fontWeight:700,color:T.text,lineHeight:1.25,maxWidth:88,wordBreak:"break-word"}}>{ph.title||"Foto do serviço"}</div>
                            <div style={{fontSize:9,color:T.muted,lineHeight:1.25,maxWidth:88,wordBreak:"break-word"}}>{ph.addedBy||batch.addedBy||"Usuário"}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>}
                {(item.timeline||[]).length>0 && [...(item.timeline||[])].reverse().map((ev,i)=>(
                  <div key={i} style={{display:"flex",gap:12,paddingBottom:10,marginBottom:10,
                    borderBottom:i<(item.timeline||[]).length-1?`1px solid ${T.border}22`:"none"}}>
                    <div style={{width:32,height:32,borderRadius:"50%",background:`${T.accent}22`,
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>
                      {ev.action?.includes("concluí")||ev.action?.includes("Concluí")?"✅":
                       ev.action?.includes("Negado")||ev.action?.includes("negado")?"❌":
                       ev.action?.includes("Aprovado")||ev.action?.includes("aprovado")?"✅":
                       ev.action?.includes("Orçamento")?"💰":
                       ev.action?.includes("Data")||ev.action?.includes("Agend")?"📅":
                       ev.action?.includes("Material")?"📦":
                       ev.action?.includes("Foto")||ev.action?.includes("foto")?"📷":
                       ev.action?.includes("nota fiscal")||ev.action?.includes("Nota fiscal")||ev.action?.includes("NF")?"📄":"📝"}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,color:T.text,fontSize:13}}>{ev.action}</div>
                      {ev.note&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>{ev.note}</div>}
                      <div style={{fontSize:10,color:T.muted,marginTop:4}}>{ev.actor} · {ev.ts?new Date(ev.ts).toLocaleString("pt-BR"):""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── NEW / EDIT FORM INLINE ────────────────────────────────── */}
      {form&&!form._new&&<div><button onClick={()=>setForm(null)} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:13,marginBottom:16}}>← Voltar</button><div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 20px"}}><div style={{fontWeight:800,color:T.text,fontSize:18,marginBottom:14}}>{form?(form._new?(form.type==="occurrence"?"Nova Ocorrência":form.type==="external"?"Novo Serviço de Terceiro":"Novo Serviço Interno"):(form.type==="occurrence"?"Editar Ocorrência":"Editar Serviço")):""}</div><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>

          {/* Title from list */}
          <Field label="Título *" col="1/-1">
            <Select value={form.title||""} onChange={e=>setForm(f=>({...f,title:e.target.value}))}>
              <option value="">Selecione o serviço...</option>
              {(SL.SERVICE_TITLES||[]).map(t=><option key={t}>{t}</option>)}
            </Select>
          </Field>

          {/* Priority — dynamic per type */}
          <Field label="Prioridade *" col="1/-1">
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {getPriorities(form.type).map(p=>(
                <div key={p.id} onClick={()=>setForm(f=>({...f,priority:p.id,...(f.type==="occurrence"?{urgency:p.label}:{} )}))}
                  style={{flex:form.type==="occurrence"&&isCompactViewport()?"1 1 calc(50% - 6px)":"1 1 auto",padding:"6px 10px",borderRadius:8,cursor:"pointer",textAlign:"center",
                    border:`2px solid ${form.priority===p.id?p.color:T.border}`,
                    background:form.priority===p.id?`${p.color}18`:T.card2,transition:"all .15s"}}>
                  <div style={{fontSize:12,fontWeight:700,color:form.priority===p.id?p.color:T.muted}}>{p.icon} {p.label}</div>
                </div>
              ))}
            </div>
          </Field>

          <Field label="Data" col={isCompactViewport()?"1/-1":undefined}><Input type="date" value={form.date||today()} onChange={e=>setForm(f=>({...f,date:e.target.value}))}/></Field>
          {!form._new&&form.type!=="occurrence"&&(
            <Field label="Status" col={isCompactViewport()?"1/-1":undefined}>
              <Select value={form.status||"Aberto"} onChange={e=>setForm(f=>({...f,status:e.target.value,nfPending:e.target.value!=="Concluído"?false:(f.nfPending||false)}))}>
                {getEditableServiceStatuses(form.type, form).map(s=><option key={s}>{s}</option>)}
              </Select>
            </Field>
          )}

          {/* Assignees — for internal and occurrence */}
          {(form.type==="internal"||form.type==="occurrence")&&(
            <Field label="Encaminhar para Colaborador(es)">
              <Select value="" onChange={e=>{ const emp=(employees||[]).find(x=>String(x.id)===String(e.target.value)); if(emp){ setForm(f=>{ const current=Array.isArray(f.assignees)?f.assignees:[]; return current.some(x=>String((x&&typeof x==="object")?x.id:x)===String(emp.id)) ? f : ({...f, assignees:[...current,{ id:emp.id, name:emp.name, username:emp.username||"", employeeId:emp.id, linkedAccountId:emp.linkedAccountId||"", userId:emp.userId||"" }]}); }); } e.target.value=""; }}>
                <option value="">+ Adicionar colaborador...</option>
                {employees.filter(e=>e.status==="Ativo"||!e.status).map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
              </Select>
              {(form.assignees||[]).length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                {(form.assignees||[]).map(a=>({ raw:a, id:String((a&&typeof a==="object")?a.id:a), label:(a&&typeof a==="object")?(a.name||a.displayName||a.id):String(a||"") })).map(entry=>(
                  <span key={entry.id||entry.label} style={{fontSize:11,background:T.accent+"22",color:T.accent,borderRadius:20,padding:"2px 8px",display:"flex",alignItems:"center",gap:4}}>
                    {entry.label}<button onClick={()=>setForm(f=>({...f,assignees:(f.assignees||[]).filter(x=>String((x&&typeof x==="object")?x.id:x)!==String(entry.id||entry.label))}))} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:12,padding:0}}>×</button>
                  </span>
                ))}
              </div>}
            </Field>
          )}

          {/* Providers — for external */}
          {form.type==="external"&&(<>
            <Field label="Prestador(es)">
              <Select value="" onChange={e=>{if(e.target.value&&!(form.providersList||[]).includes(e.target.value))setForm(f=>({...f,providersList:[...(f.providersList||[]),e.target.value]}));}}>
                <option value="">+ Adicionar prestador...</option>
                {providerDirectory.map(p=><option key={p.id} value={p.name}>{p.name} — {p.specialty || p.serviceCategories?.[0] || "Prestador"}</option>)}
              </Select>
              {(form.providersList||[]).length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                {(form.providersList||[]).map(p=>(
                  <span key={p} style={{fontSize:11,background:"#0D948822",color:"#0D9488",borderRadius:20,padding:"2px 8px",display:"flex",alignItems:"center",gap:4}}>
                    {p}<button onClick={()=>setForm(f=>({...f,providersList:(f.providersList||[]).filter(x=>x!==p)}))} style={{background:"none",border:"none",color:"#0D9488",cursor:"pointer",fontSize:12,padding:0}}>×</button>
                  </span>
                ))}
              </div>}
            </Field>
            <Field label="Responsável pelo Contato">
              <Select value={form.contactPersonId||""} onChange={e=>{ const emp=(employees||[]).find(x=>String(x.id)===String(e.target.value)); setForm(f=>({...f,contactPerson:emp?.name||"",contactPersonId:emp?.id||"",contactPersonUsername:emp?.username||""})); }}>
                <option value="">Selecione...</option>
                {employees.filter(e=>e.status==="Ativo"||!e.status).map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
              </Select>
            </Field>
            {/* Misto: also add collaborators */}
            <Field label="Colaboradores (se misto)" col="1/-1">
              <Select value="" onChange={e=>{ const emp=(employees||[]).find(x=>String(x.id)===String(e.target.value)); if(emp){ setForm(f=>{ const current=Array.isArray(f.assignees)?f.assignees:[]; return current.some(x=>String((x&&typeof x==="object")?x.id:x)===String(emp.id)) ? f : ({...f, assignees:[...current,{ id:emp.id, name:emp.name, username:emp.username||"", employeeId:emp.id, linkedAccountId:emp.linkedAccountId||"", userId:emp.userId||"" }]}); }); } e.target.value=""; }}>
                <option value="">+ Colaborador (opcional)...</option>
                {employees.filter(e=>e.status==="Ativo"||!e.status).map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
              </Select>
              {(form.assignees||[]).length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                {(form.assignees||[]).map(a=>({ raw:a, id:String((a&&typeof a==="object")?a.id:a), label:(a&&typeof a==="object")?(a.name||a.displayName||a.id):String(a||"") })).map(entry=>(
                  <span key={entry.id||entry.label} style={{fontSize:11,background:T.accent+"22",color:T.accent,borderRadius:20,padding:"2px 8px",display:"flex",alignItems:"center",gap:4}}>
                    {entry.label}<button onClick={()=>setForm(f=>({...f,assignees:(f.assignees||[]).filter(x=>String((x&&typeof x==="object")?x.id:x)!==String(entry.id||entry.label))}))} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:12,padding:0}}>×</button>
                  </span>
                ))}
              </div>}
            </Field>
          </>)}

          <Field label="Centro de custo">
            <Select value={form.costCenter||bridgeCostCenter||allowedCostCenters[0]||"Sede"} onChange={e=>setForm(f=>({...f,costCenter:e.target.value}))} disabled={bridgeScoped}>
              {(bridgeScoped ? [bridgeCostCenter] : (allowedCostCenters.length ? allowedCostCenters : (SL.COST_CENTERS||["Sede","Haras","Pecuária"]))).map(c=><option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>

          {form.type!=="occurrence"&&(
            <Field label="Categoria">
              <Select value={form.category||"other"} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {OCCURRENCE_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </Select>
            </Field>
          )}

          {/* Location */}
          <Field label="Localização / Pasto">
            <Select value={form.location||""} onChange={e=>setForm(f=>({...f,location:e.target.value,poiId:""}))}>
              <option value="">Selecione o local...</option>
              {pastures.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Ponto de Interesse">
            <Select value={form.poiId||""} onChange={e=>setForm(f=>({...f,poiId:e.target.value}))}>
              <option value="">Sem POI</option>
              {pois.map(p=><option key={p.id} value={p.id}>{p.name||p.label}</option>)}
            </Select>
          </Field>
          {(form.location || form.poiId) && <div style={{gridColumn:"1/-1",background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:"12px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}><div style={{fontWeight:700,color:T.text,fontSize:13}}>🌦️ Leitura climática para este registro</div>{formClimatePanel?.meta&&<Badge color={formClimatePanel.meta.color}>{formClimatePanel.meta.icon} {formClimatePanel.meta.label}</Badge>}</div>
            {formClimateLoading&&<div style={{fontSize:12,color:T.muted}}>Carregando leitura climática...</div>}
            {!formClimateLoading&&formClimatePanel?.error&&<div style={{fontSize:12,color:T.muted}}>{formClimatePanel.error}</div>}
            {!formClimateLoading&&!formClimatePanel?.error&&formClimatePanel&&<>
              <div style={{fontSize:12,color:T.text,fontWeight:700,marginBottom:4}}>{formClimatePanel.headline}</div>
              <div style={{fontSize:11,color:T.muted,marginBottom:8}}>{formClimatePanel.recommendation}</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{formClimatePanel.signals.map(signal=><React.Fragment key={signal.id}>{renderClimateToneBadge(signal.tone, `${signal.icon} ${signal.label}`, signal.value)}</React.Fragment>)}</div>
            </>}
          </div>}
          <Field label="Maquinário Envolvido">
            <Select value={form.machineId||""} onChange={e=>setForm(f=>({...f,machineId:e.target.value}))}>
              <option value="">Sem maquinário</option>
              {machinery.map(m=><option key={m.id} value={m.id}>{m.name}{m.plate?` (${m.plate})`:""}</option>)}
            </Select>
          </Field>
          <Field label="Tipo de Execução">
            <Select value={form.execType||""} onChange={e=>setForm(f=>({...f,execType:e.target.value}))}>
              <option value="">Selecione...</option>
              {(execTypeOptions||["Interno","Externo","Misto"]).map(t=><option key={t}>{t}</option>)}
            </Select>
          </Field>

          {form.type==="occurrence"&&(<>
            <Field label="Categoria *">
              <Select value={form.category||"other"} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {OCCURRENCE_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </Select>
            </Field>
            <Field label="Prioridade" col="1/-1">
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {getPriorities("occurrence").map(p=>(
                  <div key={p.id} onClick={()=>setForm(f=>({...f,priority:p.id,urgency:p.label}))}
                    style={{flex:isCompactViewport()?"1 1 calc(50% - 6px)":"1 1 auto",padding:"6px 10px",borderRadius:8,cursor:"pointer",textAlign:"center",
                      border:`2px solid ${(form.priority||"normal")===p.id?p.color:T.border}`,
                      background:(form.priority||"normal")===p.id?`${p.color}18`:T.card2,transition:"all .15s"}}>
                    <div style={{fontSize:12,fontWeight:700,color:(form.priority||"normal")===p.id?p.color:T.muted}}>{p.icon} {p.label}</div>
                  </div>
                ))}
              </div>
            </Field>
          </>)}

          <Field label="Descrição / Observação" col="1/-1">
            <Textarea rows={3} value={form.description||""} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Descreva o serviço, situação encontrada, observações..."/>
          </Field>

          <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between"}}>
            <div>{!form._new&&canDelete&&<Btn variant="danger" onClick={()=>requestServiceDelete(form)}>🗑️ Excluir</Btn>}</div>
            <div style={{display:"flex",gap:10}}>
              <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
              {((form&&form._new&&canCreate)||(form&&!form._new&&canEditService))&&<Btn onClick={saveService}>Salvar</Btn>}
            </div>
          </div>
        </div></div></div>}
    </div>
  );
}
}